import type { Dictionary } from "@/lib/i18n/dictionaries/en";

const nl: Dictionary = {
  // ---- /citation-styles/alphabetical/<letter> ----
  styleDirectory: {
    notFoundTitle: "Citatiestijlenoverzicht niet gevonden",
    metaTitle: "Citatiestijlen die beginnen met {label}",
    metaDescription:
      "Bekijk {count} Citation Style Language-stijlen die beginnen met {label}. Open een stijl voor echte citatievoorbeelden en een gratis generator.",
    ogSubtitle: "{count} stijlen met eigen voorbeelden en generators.",
    ogKind: "A–Z-overzicht",
    schemaName: "Citatiestijlen die beginnen met {label}",
    schemaDescription:
      "Alfabetisch overzicht van {count} Citation Style Language-stijlen die beginnen met {label}.",
    breadcrumbStyles: "Citatiestijlen",
    heading: "Citatiestijlen die beginnen met {label}",
    intro:
      "Bekijk alle {count} stijlen in dit deel van de CSL-catalogus. Elke pagina bevat de opgemaakte voorbeelden van de stijl en een gratis citatiegenerator.",
    navLabel: "Citatiestijlen van A tot Z",
    listHeading: "Alle stijlen die beginnen met {label}",
  },
  nav: {
    tools: "Hulpmiddelen",
    guides: "Handleidingen",
    styles: "Alle stijlen",
    bibliography: "Mijn literatuurlijst",
    search: "Zoeken",
    glossary: "Woordenlijst",
    home: "Home",
    menu: "Menu",
  },

  home: {
    title: "Gratis bronvermelding maken",
    subtitle: "Bijgewerkt voor 2026",
    intro:
      "Plak een DOI, URL of BibTeX-invoer — of zoek een boek — en krijg een correct opgemaakte bronvermelding in APA, MLA, Chicago, Vancouver, Harvard en duizenden andere stijlen. Volledig gratis, zonder betaald abonnement en zonder account.",
    browseAllStyles: "Bekijk alle {count} citatiestijlen →",
    featuresHeading: "Alles wat je nodig hebt om te verwijzen",
    featureStyles: "{count} stijlen",
    featureStylesDetail: "APA, MLA, Chicago en meer",
    featureBibliographies: "Meerdere literatuurlijsten",
    featureBibliographiesDetail: "Per opdracht geordend",
    featureBookSearch: "Boeken zoeken",
    featureBookSearchDetail: "Op titel of auteur",
    featureExport: "Overal naartoe exporteren",
    featureExportDetail: "BibTeX, Word, RIS, CSV",
    featureDarkMode: "Donkere modus",
    featureDarkModeDetail: "Prettig voor de ogen",
    featureNoSignup: "Geen account",
    featureNoSignupDetail: "Altijd gratis, geen betaald abonnement",
    aboutHeading:
      "Een gratis bronvermeldingsgenerator voor studenten en onderzoekers",
    aboutBody:
      "CitationEasy ondersteunt meer dan 10.000 wetenschappelijke citatiestijlen. Of je nu APA nodig hebt voor psychologie, MLA voor letteren, Chicago voor geschiedenis of Vancouver voor medische tijdschriften: de opmaak komt uit de officiële definitie van de stijl zelf, niet uit een benadering.",
    howItWorksHeading: "Hoe het werkt",
    howItWorksStep1:
      "Plak een DOI, URL, ISBN, PubMed- of arXiv-nummer of een BibTeX-invoer; je kunt ook een boek op titel zoeken.",
    howItWorksStep2:
      "Kies je citatiestijl. Typ om te zoeken tussen alle {count} stijlen.",
    howItWorksStep3:
      "Kopieer de afgeronde bronvermelding, of bewaar hem in een literatuurlijst en exporteer naar Word, BibTeX of RIS.",
    privacyNote:
      "Je literatuurlijst wordt in je eigen browser bewaard. Geen account, en er wordt niets geüpload.",
  },

  hubs: {
    toolsTitle: "Gratis hulpmiddelen voor schrijven en verwijzen",
    toolsIntro:
      "{count} hulpmiddelen die in de browser werken voor schrijven en verwijzen: generatoren voor elk brontype, woord- en tekentellers, leesbaarheidsanalyse, opmaakhulpen en converters. Allemaal gratis en direct bruikbaar.",
    stylesTitle: "Blader door alle citatiestijlen",
    stylesIntro:
      "Alle {count} stijlen uit het Citation Style Language-project, doorzoekbaar en te filteren op land, vakgebied en discipline.",
    guidesTitle: "Handleidingen voor verwijzen en academisch schrijven",
    guidesIntro:
      "Uitgebreide handleidingen over het verwijzen naar elk brontype, het beoordelen van wat je vindt en beter schrijven.",
    howToCiteTitle: "Hoe verwijs je naar een bron",
    howToCiteIntro:
      "Stapsgewijze pagina's die elk gangbaar brontype koppelen aan de belangrijkste citatiestijlen, elk met een echt uitgewerkt voorbeeld.",
    faqTitle: "Veelgestelde vragen over verwijzen",
    faqIntro:
      "Korte, kloppende antwoorden op de vragen die studenten echt hebben bij het verwijzen.",
    glossaryTitle: "Woordenlijst voor bronvermelding",
    glossaryIntro:
      "Heldere definities van de termen die in stijlhandleidingen en opdrachtbeschrijvingen voorkomen.",
    openHub: "Openen",
  },

  styleLanding: {
    metaTitle: "Gratis {style}-generator voor bronvermeldingen",
    metaDescription:
      "Maak gratis {style}-bronvermeldingen. Plak een DOI, URL, ISBN of BibTeX en krijg de literatuurverwijzing en de verwijzing in de tekst in het juiste {style}-formaat.",
    heading: "{style}-generator voor bronvermeldingen",
    intro:
      "Maak met één klik correcte {style}-bronvermeldingen voor boeken, tijdschriftartikelen en websites. Gratis en zonder account.",
    aboutHeading: "Over {style}",
    whenToUseHeading: "Wanneer je {style} gebruikt",
    factsHeading: "{style} in het kort",
    factEdition: "Editie",
    factSystem: "Verwijssysteem",
    factInText: "Vorm in de tekst",
    factReferenceList: "Naam van de lijst",
    factFields: "Gebruikelijk in",
    systemAuthorDate: "Auteur–jaar",
    systemNumbered: "Genummerd",
    systemFootnotes: "Voetnoten",
    howToHeading: "Zo gebruik je deze {style}-generator",
    howToStep1:
      "Plak hierboven een URL, DOI, ISBN of BibTeX-invoer, of zoek een boek op titel.",
    howToStep2:
      "{style} staat op deze pagina al ingesteld, dus je kunt meteen genereren.",
    howToStep3:
      "Kopieer het resultaat naar je document, of bewaar het en exporteer later de hele lijst.",
    relatedHeading: "Meer over {style}",
    englishOnlyNote:
      "Uitgebreide {style}-handleidingen en voorbeelden per brontype zijn in het Engels beschikbaar.",
  },

  // The per-style pages at `/<locale>/citation-style/<slug>` — one per catalog style.
  //
  // The formatted examples the page is built around (the style's own output for a
  // fixed journal article, book and website) and the style's own name stay in
  // `src/data/citation-styles.ts` and `src/data/style-examples-generated.json`. They
  // are language-neutral: a citation rendered by the style's own CSL definition looks
  // the same whatever language the reader speaks, and "translating" one would make the
  // page wrong.
  //
  // The rest of what `src/data/**` supplies to these pages is NOT like that — see the
  // `generic*`, `discipline*` and `internationalLabel` keys below.
  citationStylePage: {
    notFoundTitle: "Citatiestijl niet gevonden",
    metaTitle:
      "{style}-generator voor bronvermeldingen - Gratis {style}-opmaak",
    metaFormatAndFields: "{style} is een {format}-stijl, gebruikt in {fields}.",
    metaFormatOnly: "{style} is een {format}-stijl.",
    metaFieldsOnly: "{style} wordt gebruikt in {fields}.",
    metaPromiseNote:
      "Bekijk een echte {style}-voetnoot en maak gratis je eigen bronvermelding.",
    metaPromiseExamples:
      "Bekijk echte {style}-voorbeelden voor een tijdschriftartikel, boek en website, en maak gratis je eigen bronvermelding.",
    ogKindFormat: "{format}-stijl",
    ogKindDefault: "Citatiestijl",
    // Joins the style's discipline list: "psychology, education and nursing".
    disciplineConjunction: "en",
    // ---- Data-derived facts that are nonetheless mail-merged prose ----
    //
    // Every entry in `src/data/citation-styles-generated.ts` carries exactly the
    // two sentences below with their own name spliced in, exactly one region value
    // ("International" — that is `internationalLabel` above, reused rather than
    // duplicated), and discipline tokens drawn from a closed 26-word vocabulary. None
    // of that is a citation, so none of it is language-neutral the way the formatted
    // examples are. Left in `src/data/**` it renders English facts inside translated
    // chrome, which is precisely the hybrid page this tree exists to avoid.
    //
    // The page substitutes the two templates ONLY when a style's stored text is
    // byte-identical to the English template with its own name interpolated, so the
    // ~100 hand-curated styles keep their bespoke English descriptions instead of
    // being flattened into a generic sentence. `{name}` is the style's full name.
    genericDescription:
      "Citatiestijl voor {name}. Uit de Citation Style Language-repository.",
    genericWhenUsed:
      "Bij het indienen bij of citeren in publicaties die de stijl {name} gebruiken.",

    // The 26 discipline tokens `style-examples-generated.json` uses. Each English
    // value is the exact string the page printed before these keys existed — the raw
    // token with hyphens turned into spaces — so `generic-base` reads "generic base"
    // and `political_science` keeps its underscore. An unknown token (a new one from
    // upstream CSL) falls back to that same prettified form rather than to a key name.
    disciplineAnthropology: "antropologie",
    disciplineAstronomy: "sterrenkunde",
    disciplineBiology: "biologie",
    disciplineBotany: "plantkunde",
    disciplineChemistry: "scheikunde",
    disciplineCommunications: "communicatiewetenschap",
    disciplineEngineering: "techniek",
    disciplineGenericBase: "algemeen",
    disciplineGeography: "geografie",
    disciplineGeology: "geologie",
    disciplineHistory: "geschiedenis",
    disciplineHumanities: "geesteswetenschappen",
    disciplineLaw: "rechten",
    disciplineLinguistics: "taalkunde",
    disciplineLiterature: "letterkunde",
    disciplineMath: "wiskunde",
    disciplineMedicine: "geneeskunde",
    disciplinePhilosophy: "filosofie",
    disciplinePhysics: "natuurkunde",
    disciplinePoliticalScience: "politicologie",
    disciplinePsychology: "psychologie",
    disciplineScience: "natuurwetenschappen",
    disciplineSocialScience: "sociale wetenschappen",
    disciplineSociology: "sociologie",
    disciplineTheology: "theologie",
    disciplineZoology: "zoölogie",

    // Plain-language descriptions of the citation-format classes the CSL definitions
    // declare. `{format}` in the fallback is the raw class name from the data.
    formatClassAuthorDate:
      "een auteur-jaarstijl: de verwijzing in de tekst noemt de auteur en het jaar, en de literatuurlijst is alfabetisch gerangschikt op auteur",
    formatClassNumeric:
      "een numerieke stijl: bronnen worden genummerd in de volgorde waarin ze voor het eerst worden aangehaald, en de verwijzingen in de tekst gebruiken die nummers",
    formatClassNote:
      "een stijl met noten: bronvermeldingen staan in voet- of eindnoten in plaats van tussen haakjes in de tekst",
    formatClassLabel:
      "een labelstijl: de verwijzing in de tekst gebruikt een compact label opgebouwd uit de auteursnaam en het jaar",
    formatClassAuthor:
      "een auteurstijl: de verwijzing in de tekst noemt alleen de auteur, met alle details in de literatuurlijst",
    formatClassOther: "een {format}-stijl",
    breadcrumbStyles: "Citatiestijlen",
    internationalLabel: "Internationaal",
    heading: "{style}-generator voor bronvermeldingen",
    intro:
      "Maak met één klik correcte {style}-bronvermeldingen voor boeken, artikelen en websites.",
    examplesHeading: "Voorbeelden van {style}-bronvermeldingen",
    examplesIntro: "Dezelfde drie bronnen opgemaakt in {style}:",
    examplesIntroWithFormat:
      "Dezelfde drie bronnen opgemaakt in {style} — {format}:",
    examplesNoteOnlyIntro:
      "{style} is een stijl met noten: er is geen literatuurlijst en elke bronvermelding is een voetnoot. Hier is ons voorbeeld van een tijdschriftartikel geciteerd in {style}:",
    labelJournalArticle: "Tijdschriftartikel",
    labelBook: "Boek",
    labelWebsite: "Website",
    labelFootnote: "Voetnoot (tijdschriftartikel)",
    labelInText: "Verwijzing in de tekst (voor het tijdschriftartikel)",
    aboutHeading: "Over {style}",
    whenToUseLabel: "Wanneer je {style} gebruikt:",
    commonInLabel: "Gebruikelijk in:",
    subjectAreasLabel: "Vakgebieden:",
    formattingRulesLabel: "Opmaakregels:",
    parentStylePrefix: "{style} volgt de opmaak van",
    parentStyleSuffix: ", de basisstijl waarop deze is gebaseerd.",
    howToHeading: "Zo gebruik je deze {style}-generator",
    howToBody:
      'Plak een DOI, een URL naar een nieuwsartikel of webpagina, of ruwe BibTeX in het vak hierboven. Je kunt ook een boek op titel of auteur zoeken en citeren via het ISBN. Kies "{style}" in het stijlmenu (dat staat op deze pagina al vooraf ingesteld) en klik op "Bronvermelding maken". Kopieer het resultaat naar je literatuurlijst of bibliografie. Je kunt de gegenereerde vermelding bewerken (bijvoorbeeld een titel verbeteren of een datum toevoegen) voordat je kopieert.',
    faqHeading: "Veelgestelde vragen over {style}",
    faqWhatIsQ: "Wat is {style}?",
    faqWhenQ: "Wanneer gebruik ik {style}?",
    faqInTextQ: "Hoe ziet een verwijzing in de tekst eruit in {style}?",
    faqInTextA:
      "{style} is {format}. Het voorbeeldartikel citeren in de tekst ziet er zo uit: {example}",
    faqReferenceQ:
      "Hoe ziet een vermelding in de {style}-literatuurlijst eruit?",
    faqReferenceA:
      "Een tijdschriftartikel opgemaakt in {style} ziet er zo uit: {example}",
    faqWhereQ: "Waar wordt {style} vooral gebruikt?",
    faqWhereA: "{style} wordt {region} gebruikt. {whenUsed}",
    faqRegionDefault: "internationaal",
    faqGenerateQ: "Hoe maak ik een {style}-bronvermelding?",
    faqGenerateA:
      "Gebruik de gratis generator op deze pagina: plak een DOI, URL (bijvoorbeeld van een nieuwsartikel) of BibTeX, of zoek een boek op titel of auteur. Selecteer {style} als citatiestijl en klik op Bronvermelding maken om een opgemaakte referentie te krijgen die je in je werkstuk of literatuurlijst kunt plakken.",
    footerSupports:
      "ondersteunt talloze citatiestijlen van over de hele wereld. Onze generator gebruikt Citation Style Language-stijlen (CSL) uit de",
    footerRepoLink: "officiële CSL-repository",
  },

  // The source × style pages at `/<locale>/how-to-cite/<slug>` — 1,290 combinations.
  //
  // Same division as above: the citation templates, the worked examples generated from
  // each style's own CSL definition, the in-text samples and the hand-checked
  // formatting rules stay in the page module and in
  // `src/data/how-to-cite-examples-generated.json`. They are the citations themselves,
  // not prose about them. Source names, style names and editions come from
  // `src/data/how-to-cite.ts` and arrive here as placeholder values.
  crossLinks: {
    generateHeading: "Deze bronvermelding genereren",
    generateBody:
      "Plak een URL, DOI, ISBN of de brongegevens en krijg een kant-en-klare {style}-bronvermelding voor {source}.",
    generateCta: "{tool} openen →",
    guideHeading: "Verder lezen",
    guideCta: "Lees de volledige gids: {guide}",
    howToCiteHeading: "Zo citeer je {source} in elke stijl",
    howToCiteBody:
      "Opmaakregels, sjablonen en uitgewerkte voorbeelden voor de meest gevraagde stijlen.",
    howToCiteMore: "Alle brontypen en stijlen →",
    citeNowHeading: "Citeer nu {source} in {style}",
    citeNowHowTo: "Opmaakregels en uitgewerkte voorbeelden",
    citeNowTool: "Genereer het met de {tool}",
    citeNowHub: "{style}-bronvermeldingsgenerator",
    hubSourcesHeading: "Veelvoorkomende bronnen citeren in {style}",
    hubGuidesHeading: "{style}-gidsen",
    hubCompareHeading: "{style} vergelijken met andere stijlen",
    moreInCollection: "Meer hulpmiddelen: {collection}",
    seeAllInCollection: "Alles bekijken: {collection}",
  },

  howToCitePage: {
    notFoundTitle: "Niet gevonden",
    title: "Hoe verwijs je naar {source} in {style} ({edition})",
    metaTitle:
      "Hoe verwijs je naar {source} in {style} [{edition}, bijgewerkt 2026]",
    metaDescription:
      "Leer hoe je {description} citeert in {style}-opmaak ({edition}). Gratis voorbeelden, sjablonen en handleidingen voor verwijzingen in de tekst.",
    keywordCite: "{source} citeren {style}",
    keywordCitation: "{style} {source} bronvermelding",
    keywordHowTo: "{source} citeren",
    keywordExample: "{style} voorbeeld bronvermelding",
    keywordReference: "{source} referentie {style}",
    // What each style calls the list at the back of the paper, in the two casings the
    // prose needs. MLA says "Works Cited", Chicago and the legal styles "Bibliography".
    refListWorksCited: "Geciteerde werken",
    refListWorksCitedPage: "geciteerde werken",
    refListBibliography: "Bibliografie",
    refListBibliographyLower: "bibliografie",
    refListReferenceList: "Literatuurlijst",
    refListReferenceListLower: "literatuurlijst",
    backLink: "← Alle gidsen voor bronvermelding",
    lead: "Leer hoe je {description} citeert in {style}-opmaak ({edition}). Deze handleiding bevat de opmaak van de literatuurlijst, voorbeelden van verwijzingen in de tekst en de belangrijkste opmaakregels.",
    referenceFormatHeading: "{style} {list}-opmaak",
    referenceFormatIntro:
      "Dit is de standaard {style}-opmaak voor het citeren van {source} in je {list}:",
    noTemplateNote: "Volg het standaardsjabloon van {style} voor dit brontype.",
    workedHeading: "{style}-voorbeeld voor {source}",
    workedIntro:
      "Een volledige {style}-bronvermelding voor {source}, opgemaakt met de officiële {style} CSL-stijldefinitie — dezelfde engine die de generator op deze site aandrijft:",
    workedNoteOnlyIntro:
      "{style} geeft {source} geen aparte vermelding in de sectie {list} — de bron wordt alleen in een noot geciteerd. Hier is die noot, opgemaakt met de officiële {style} CSL-stijldefinitie:",
    workedOscolaNote:
      "De OSCOLA-uitvoer die deze site genereert volgt de CSL-stijl van de 4e editie, omdat in de repository van Citation Style Language nog geen CSL-stijl van de 5e editie is gepubliceerd. Pas hem met de hand aan: vervang elke ibid door de korte naam en (n X), gebruik de rechtbankidentificaties van de 5e editie, zet de verkorte vorm van een wet tussen enkele aanhalingstekens en geef een permanente link of DOI de voorkeur boven een raadpleegdatum",
    workedCheckedIntro:
      "Een volledige {style}-bronvermelding voor {source}, met de hand geschreven en gecontroleerd aan de hand van de officiële {style}-richtlijnen voor dit brontype:",
    workedCheckedNoteOnlyIntro:
      "{style} geeft {source} geen aparte vermelding in de sectie {list} — de bron wordt alleen in een noot geciteerd. Hier is die noot, met de hand geschreven en gecontroleerd aan de hand van de officiële {style}-richtlijnen:",
    workedInTextLabel: "Bijbehorende verwijzing in de tekst:",
    inTextHeading: "Verwijzing in de tekst in {style}",
    inTextIntro:
      "Gebruik een van deze vormen wanneer je in je werkstuk naar deze bron verwijst:",
    inTextParenthetical: "Tussen haakjes",
    inTextNarrative: "Verhalend",
    rulesHeading: "Belangrijkste opmaakregels voor {style}",
    stepsHeading: "Stap-voor-stap-handleiding",
    step1Label: "Verzamel brongegevens:",
    step1Body:
      "Verzamel de auteursnaam, titel, publicatiedatum en URL of DOI van je {source}.",
    step2Label: "Maak de bronvermelding op:",
    step2Body:
      "Rangschik de onderdelen volgens het hierboven getoonde {style}-sjabloon ({edition}).",
    step3Label: "Maak de verwijzing in de tekst:",
    step3Body:
      "Voeg op de plek waar je naar deze bron verwijst een verwijzing tussen haakjes of een verhalende verwijzing toe.",
    step4Label: "Voeg toe aan je {list}:",
    step4Body:
      "Neem de volledige, opgemaakte bronvermelding op aan het einde van je werkstuk.",
    step5Label: "Controleer de opmaak nog eens:",
    step5Body:
      "Controleer of cursivering, leestekens, hoofdlettergebruik en inspringing voldoen aan de eisen van {style}.",
    ctaHeading: "Maak deze bronvermelding automatisch",
    ctaBody:
      "Sla het handmatig opmaken over. Plak een DOI, URL of ISBN in CitationEasy en krijg in enkele seconden een perfect opgemaakte {style}-bronvermelding.",
    ctaButton: "Nu citeren in {style} →",
    otherStylesHeading: "Zo citeer je {source} in andere stijlen",
    comboLink: "{source} in {style}",
    otherSourcesHeading: "Andere bronnen citeren in {style}",
    resourcesHeading: "Meer hulpbronnen",
    resourceGenerator:
      "Gratis generator voor bronvermeldingen ({count} stijlen)",
    resourceTools: "Gratis schrijfhulpmiddelen",
    resourceCompare: "Citatiestijlen vergelijken",
    resourceGuides: "Alle gidsen voor bronvermelding",
    resourceCheatSheets: "Spiekbriefjes (APA, MLA, Chicago)",
    resourceGlossary:
      "Woordenlijst bronvermelding (et al., ibid., DOI, hangende inspringing)",
    // HowTo structured data. Deliberately worded differently from the visible steps
    // above: the schema describes the task generically, the page walks the reader
    // through this particular source and style.
    schemaStep1Name: "Verzamel brongegevens",
    schemaStep1Text:
      "Verzamel alle benodigde gegevens voor je {source}, waaronder auteur, titel, datum en URL of DOI.",
    schemaStep2Name: "Opmaken in {style}",
    schemaStep2Text:
      "Volg de opmaakregels van {style} ({edition}) om de onderdelen van de bronvermelding in de juiste volgorde te zetten.",
    schemaStep3Name: "Maak de verwijzing in de tekst",
    schemaStep3Text:
      "Voeg in je werkstuk de bijbehorende verwijzing in de tekst toe, volgens de {style}-opmaak.",
    schemaStep4Name: "Toevoegen aan de literatuurlijst",
    schemaStep4Text:
      "Neem de volledige bronvermelding op in je {list} aan het einde van je werkstuk.",
    faqHowQ: "Hoe citeer ik {source} in {style}?",
    faqHowA:
      "Om {source} in {style} ({edition}) te citeren, neem je de auteur, titel, datum en vindplaats van de bron op. Gebruik onze gratis generator voor automatische opmaak.",
    faqFormatQ:
      "Wat is de {style}-opmaak voor een bronvermelding van {source}?",
    faqFormatA: "De {style}-opmaak voor {source} is: {example}",
    faqFormatFallback:
      "De {style}-opmaak vereist specifieke onderdelen, zoals auteur, titel, datum en vindplaats, in een vaste volgorde.",
    faqInTextQ:
      "Hoe ziet de verwijzing in de tekst eruit voor {source} in {style}?",
    faqInTextA:
      "In {style} ziet de verwijzing in de tekst voor het bovenstaande voorbeeld er zo uit: {example}",
  },

  // The 43 source types the how-to-cite pages are built from (`SOURCE_TYPES` in
  // `src/data/how-to-cite.ts`), in the five forms those pages print. Each form is a whole
  // phrase, never an article and a noun joined in code: an article agrees with its noun
  // in gender, number and case, elides in French and Italian, and has no counterpart
  // at all in Polish, Russian or Japanese.
  //
  // - `…Name` ("Book") is the type on its own, as a label: `howToCitePage.comboLink`.
  //   English also lower-cases it into the `keyword*` templates.
  // - `…Title` ("a Book") is the type as the object of "cite" in a title or heading:
  //   `title`, `metaTitle`, `workedHeading` and `otherStylesHeading`, plus
  //   `crossLinks.generateBody`, `howToCiteHeading` and `citeNowHeading`. Through
  //   `title` it is also the HowTo name, the OG card and every link to the page.
  // - `…Phrase` ("a book") is the same inside a sentence: `referenceFormatIntro`,
  //   `workedIntro`, `workedNoteOnlyIntro` and the five `faq*` questions and answers.
  // - `…Noun` ("book") is the bare noun inside a sentence, where English puts "your" in
  //   front of it: `step1Body` and `schemaStep1Text`. A translation also uses it in the
  //   `keyword*` templates, which read as search queries.
  // - `…Description` is what the type covers: `{description}` in `lead` and
  //   `metaDescription`.
  //
  // A language with case picks one case per form and words every template above around
  // it — German writes Title and Phrase in the accusative, Polish and Russian write Name
  // and Noun in the nominative. Changing a form's case means re-reading every template
  // that receives it. A key missing here falls back to the English data
  // (`howToCiteSourceForms`), so a new source type renders in English, not as a key.
  // Dutch: Title and Phrase carry a lower-case article ("een boek", "de Bijbel"); Noun is bare, after "je".
  howToCiteSources: {
    websiteName: "Website",
    websiteTitle: "een website",
    websitePhrase: "een website",
    websiteNoun: "website",
    websiteDescription: "webpagina's, onlineartikelen en webcontent",

    bookName: "Boek",
    bookTitle: "een boek",
    bookPhrase: "een boek",
    bookNoun: "boek",
    bookDescription: "gedrukte en elektronische boeken",

    journalArticleName: "Tijdschriftartikel",
    journalArticleTitle: "een tijdschriftartikel",
    journalArticlePhrase: "een tijdschriftartikel",
    journalArticleNoun: "tijdschriftartikel",
    journalArticleDescription:
      "peer-reviewed en wetenschappelijke tijdschriftartikelen",

    youtubeVideoName: "YouTube-video",
    youtubeVideoTitle: "een YouTube-video",
    youtubeVideoPhrase: "een YouTube-video",
    youtubeVideoNoun: "YouTube-video",
    youtubeVideoDescription: "YouTube-video's en andere onlinevideo's",

    podcastName: "Podcast",
    podcastTitle: "een podcast",
    podcastPhrase: "een podcast",
    podcastNoun: "podcast",
    podcastDescription: "podcastafleveringen en -series",

    newspaperArticleName: "Krantenartikel",
    newspaperArticleTitle: "een krantenartikel",
    newspaperArticlePhrase: "een krantenartikel",
    newspaperArticleNoun: "krantenartikel",
    newspaperArticleDescription: "gedrukte en online krantenartikelen",

    magazineArticleName: "Magazineartikel",
    magazineArticleTitle: "een magazineartikel",
    magazineArticlePhrase: "een magazineartikel",
    magazineArticleNoun: "magazineartikel",
    magazineArticleDescription: "gedrukte en online magazineartikelen",

    blogPostName: "Blogbericht",
    blogPostTitle: "een blogbericht",
    blogPostPhrase: "een blogbericht",
    blogPostNoun: "blogbericht",
    blogPostDescription: "blogberichten en opiniestukken op internet",

    wikipediaName: "Wikipedia",
    wikipediaTitle: "Wikipedia",
    wikipediaPhrase: "Wikipedia",
    wikipediaNoun: "Wikipedia-artikel",
    wikipediaDescription: "Wikipedia-artikelen en andere wikipagina's",

    pdfName: "PDF",
    pdfTitle: "een PDF",
    pdfPhrase: "een PDF",
    pdfNoun: "PDF",
    pdfDescription: "PDF-documenten en -rapporten",

    interviewName: "Interview",
    interviewTitle: "een interview",
    interviewPhrase: "een interview",
    interviewNoun: "interview",
    interviewDescription:
      "persoonlijke, gepubliceerde en uitgezonden interviews",

    socialMediaName: "Bericht op sociale media",
    socialMediaTitle: "een bericht op sociale media",
    socialMediaPhrase: "een bericht op sociale media",
    socialMediaNoun: "bericht op sociale media",
    socialMediaDescription:
      "tweets, Instagramposts, Facebookberichten en TikTok-video's",

    governmentDocumentName: "Overheidsdocument",
    governmentDocumentTitle: "een overheidsdocument",
    governmentDocumentPhrase: "een overheidsdocument",
    governmentDocumentNoun: "overheidsdocument",
    governmentDocumentDescription:
      "overheidsrapporten, wetgeving en officiële documenten",

    dissertationName: "Proefschrift of scriptie",
    dissertationTitle: "een proefschrift of scriptie",
    dissertationPhrase: "een proefschrift of scriptie",
    dissertationNoun: "proefschrift of scriptie",
    dissertationDescription: "proefschriften en masterscripties",

    conferencePaperName: "Congresbijdrage",
    conferencePaperTitle: "een congresbijdrage",
    conferencePaperPhrase: "een congresbijdrage",
    conferencePaperNoun: "congresbijdrage",
    conferencePaperDescription: "congresbundels en presentaties",

    lectureNotesName: "Hoorcollege of PowerPoint",
    lectureNotesTitle: "een hoorcollege of PowerPoint",
    lectureNotesPhrase: "een hoorcollege of PowerPoint",
    lectureNotesNoun: "hoorcollege of PowerPoint",
    lectureNotesDescription: "colleges, dia's en cursusmateriaal",

    webinarName: "Webinar",
    webinarTitle: "een webinar",
    webinarPhrase: "een webinar",
    webinarNoun: "webinar",
    webinarDescription: "live en opgenomen webinars en onlineseminars",

    onlineCourseName: "Onlinecursus",
    onlineCourseTitle: "een onlinecursus",
    onlineCoursePhrase: "een onlinecursus",
    onlineCourseNoun: "onlinecursus",
    onlineCourseDescription:
      "onlinecursussen en MOOC's (Coursera, edX, Udemy, FutureLearn)",

    presentationSlidesName: "PowerPoint-presentatie",
    presentationSlidesTitle: "een PowerPoint-presentatie",
    presentationSlidesPhrase: "een PowerPoint-presentatie",
    presentationSlidesNoun: "PowerPoint-presentatie",
    presentationSlidesDescription:
      "PowerPoint-presentaties en andere diapresentaties",

    imageName: "Afbeelding of foto",
    imageTitle: "een afbeelding of foto",
    imagePhrase: "een afbeelding of foto",
    imageNoun: "afbeelding of foto",
    imageDescription:
      "foto's, kunstwerken, grafieken en afbeeldingen van internet",

    movieName: "Film",
    movieTitle: "een film",
    moviePhrase: "een film",
    movieNoun: "film",
    movieDescription: "een film",

    documentaryName: "Documentaire",
    documentaryTitle: "een documentaire",
    documentaryPhrase: "een documentaire",
    documentaryNoun: "documentaire",
    documentaryDescription: "documentaires en non-fictiefilms",

    tvShowName: "Tv-programma",
    tvShowTitle: "een tv-programma",
    tvShowPhrase: "een tv-programma",
    tvShowNoun: "tv-programma",
    tvShowDescription: "een tv-programma of -serie",

    songName: "Lied of muziekstuk",
    songTitle: "een lied of muziekstuk",
    songPhrase: "een lied of muziekstuk",
    songNoun: "lied of muziekstuk",
    songDescription: "een lied of muziekopname",

    poemName: "Gedicht",
    poemTitle: "een gedicht",
    poemPhrase: "een gedicht",
    poemNoun: "gedicht",
    poemDescription:
      "gedichten, verzen en poëzie uit bloemlezingen of verzamelbundels",

    courtCaseName: "Rechtszaak",
    courtCaseTitle: "een rechtszaak",
    courtCasePhrase: "een rechtszaak",
    courtCaseNoun: "rechtszaak",
    courtCaseDescription: "een rechtszaak of gerechtelijke uitspraak",

    patentName: "Octrooi",
    patentTitle: "een octrooi",
    patentPhrase: "een octrooi",
    patentNoun: "octrooi",
    patentDescription:
      "een octrooi of een andere registratie van intellectueel eigendom",

    encyclopediaName: "Encyclopedieartikel",
    encyclopediaTitle: "een encyclopedieartikel",
    encyclopediaPhrase: "een encyclopedieartikel",
    encyclopediaNoun: "encyclopedieartikel",
    encyclopediaDescription: "een encyclopedieartikel of -lemma",

    dictionaryName: "Woordenboeklemma",
    dictionaryTitle: "een woordenboeklemma",
    dictionaryPhrase: "een woordenboeklemma",
    dictionaryNoun: "woordenboeklemma",
    dictionaryDescription: "een woordenboekdefinitie of -lemma",

    datasetName: "Dataset",
    datasetTitle: "een dataset",
    datasetPhrase: "een dataset",
    datasetNoun: "dataset",
    datasetDescription: "een dataset of database",

    softwareName: "Software of app",
    softwareTitle: "software of een app",
    softwarePhrase: "software of een app",
    softwareNoun: "software of app",
    softwareDescription: "software, een app of een computerprogramma",

    videoGameName: "Videogame",
    videoGameTitle: "een videogame",
    videoGamePhrase: "een videogame",
    videoGameNoun: "videogame",
    videoGameDescription: "videogames en interactieve spelsoftware",

    pressReleaseName: "Persbericht",
    pressReleaseTitle: "een persbericht",
    pressReleasePhrase: "een persbericht",
    pressReleaseNoun: "persbericht",
    pressReleaseDescription: "een persbericht of nieuwsbericht",

    reportName: "Rapport",
    reportTitle: "een rapport",
    reportPhrase: "een rapport",
    reportNoun: "rapport",
    reportDescription: "een rapport of whitepaper",

    preprintName: "Preprint",
    preprintTitle: "een preprint",
    preprintPhrase: "een preprint",
    preprintNoun: "preprint",
    preprintDescription:
      "artikelen op arXiv, bioRxiv en andere preprintservers",

    bookChapterName: "Boekhoofdstuk",
    bookChapterTitle: "een boekhoofdstuk",
    bookChapterPhrase: "een boekhoofdstuk",
    bookChapterNoun: "boekhoofdstuk",
    bookChapterDescription: "hoofdstukken en essays in verzamelbundels",

    chatgptName: "ChatGPT",
    chatgptTitle: "ChatGPT",
    chatgptPhrase: "ChatGPT",
    chatgptNoun: "ChatGPT-antwoord",
    chatgptDescription: "ChatGPT en andere generatieve AI-tools",

    bibleName: "Bijbel",
    bibleTitle: "de Bijbel",
    biblePhrase: "de Bijbel",
    bibleNoun: "bijbel",
    bibleDescription:
      "de Bijbel, heilige schriften en andere religieuze teksten",

    tedTalkName: "TED-talk",
    tedTalkTitle: "een TED-talk",
    tedTalkPhrase: "een TED-talk",
    tedTalkNoun: "TED-talk",
    tedTalkDescription: "TED-talks en opgenomen congreslezingen",

    emailName: "E-mail",
    emailTitle: "een e-mail",
    emailPhrase: "een e-mail",
    emailNoun: "e-mail",
    emailDescription: "e-mails, brieven en persoonlijke communicatie",

    redditName: "Reddit- of forumbericht",
    redditTitle: "een Reddit- of forumbericht",
    redditPhrase: "een Redditbericht",
    redditNoun: "Redditbericht",
    redditDescription:
      "Redditdiscussies, AMA's en berichten op onlineforums en discussieborden",

    tweetName: "Bericht op X (Twitter)",
    tweetTitle: "een bericht op X (Twitter)",
    tweetPhrase: "een bericht op X",
    tweetNoun: "X-bericht",
    tweetDescription: "tweets en berichten op X (voorheen Twitter)",

    mapName: "Kaart",
    mapTitle: "een kaart",
    mapPhrase: "een kaart",
    mapNoun: "kaart",
    mapDescription:
      "gedrukte kaarten, atlassen en dynamische kaarten zoals Google Maps",

    claudeName: "Claude",
    claudeTitle: "Claude",
    claudePhrase: "Claude",
    claudeNoun: "Claude-antwoord",
    claudeDescription: "Claude-chats en andere AI-uitvoer van Anthropic",

    geminiName: "Gemini",
    geminiTitle: "Gemini",
    geminiPhrase: "Gemini",
    geminiNoun: "Gemini-antwoord",
    geminiDescription: "Google Gemini-chats en AI-uitvoer",

    copilotName: "Microsoft Copilot",
    copilotTitle: "Microsoft Copilot",
    copilotPhrase: "Microsoft Copilot",
    copilotNoun: "Microsoft Copilot-antwoord",
    copilotDescription: "Microsoft Copilot-chats en AI-antwoorden",

    perplexityName: "Perplexity",
    perplexityTitle: "Perplexity",
    perplexityPhrase: "Perplexity",
    perplexityNoun: "Perplexity-antwoord",
    perplexityDescription:
      "Perplexity-antwoorden, threads en de bronnen die ze aanhalen",

    aiImageName: "Door AI gegenereerde afbeelding",
    aiImageTitle: "een door AI gegenereerde afbeelding",
    aiImagePhrase: "een door AI gegenereerde afbeelding",
    aiImageNoun: "door AI gegenereerde afbeelding",
    aiImageDescription:
      "afbeeldingen gemaakt met DALL·E, Midjourney, Gemini en andere AI-tools",

    tiktokName: "TikTok-video",
    tiktokTitle: "een TikTok-video",
    tiktokPhrase: "een TikTok-video",
    tiktokNoun: "TikTok-video",
    tiktokDescription: "TikTok-video's en berichten van makers",

    instagramName: "Instagram-post",
    instagramTitle: "een Instagram-post",
    instagramPhrase: "een Instagram-post",
    instagramNoun: "Instagram-post",
    instagramDescription: "Instagram-posts, reels en stories",

    statuteName: "Wet of wettelijke regeling",
    statuteTitle: "een wet of wettelijke regeling",
    statutePhrase: "een wet of wettelijke regeling",
    statuteNoun: "wet of wettelijke regeling",
    statuteDescription: "wetten, verordeningen en gecodificeerde regelgeving",

    isoStandardName: "ISO-norm",
    isoStandardTitle: "een ISO-norm",
    isoStandardPhrase: "een ISO-norm",
    isoStandardNoun: "ISO-norm",
    isoStandardDescription:
      "ISO-, IEC- en andere gepubliceerde technische normen",

    quranName: "Koran",
    quranTitle: "de Koran",
    quranPhrase: "de Koran",
    quranNoun: "koran",
    quranDescription: "de Koran, in het Arabisch of in vertaling",

    ebookNoPageNumbersName: "E-book zonder paginanummers",
    ebookNoPageNumbersTitle: "een e-book zonder paginanummers",
    ebookNoPageNumbersPhrase: "een e-book zonder paginanummers",
    ebookNoPageNumbersNoun: "e-book zonder paginanummers",
    ebookNoPageNumbersDescription:
      "Kindle-, EPUB- en online e-books zonder vaste paginanummers",

    speechName: "Toespraak",
    speechTitle: "een toespraak",
    speechPhrase: "een toespraak",
    speechNoun: "toespraak",
    speechDescription: "toespraken, redevoeringen en preken, live of opgenomen",

    letterName: "Brief",
    letterTitle: "een brief",
    letterPhrase: "een brief",
    letterNoun: "brief",
    letterDescription:
      "gepubliceerde brieven, brieven in archieven en open brieven",

    secondarySourceName: "Secundaire bron",
    secondarySourceTitle: "een secundaire bron",
    secondarySourcePhrase: "een secundaire bron",
    secondarySourceNoun: "secundaire bron",
    secondarySourceDescription:
      "een bron die je geciteerd of aangehaald vond in een ander werk",
  },

  // The three browse-by-facet pages under `/<locale>/citation-styles/…` — 32 academic
  // fields, 68 countries and 39 subject categories, 139 pages in one section because
  // they share a shell, a breadcrumb trail and a styles browser.
  //
  // The style NAMES, slugs and the CSL catalog itself stay in `src/data/**`: those are
  // subject matter. What the facets themselves are called is not — "Political Science",
  // "United Kingdom" and "Film & Media" are ordinary nouns that every reader expects in
  // their own language, and there are only 139 of them in total (see the `academicFields`,
  // `regions` and `styleCategories` sections below).
  taxonomyPage: {
    // ---- /citation-styles/field/<slug> ----
    fieldNotFoundTitle: "Niet gevonden",
    fieldMetaTitle:
      "Citatiestijlen voor {field} - De beste bronvermeldingsstijlen [2026]",
    fieldMetaDescription:
      "Ontdek de meest gebruikte citatiestijlen in {field}. Vind het juiste format voor je werkstuk, scriptie of publicatie in {fieldLower}.",
    fieldOgTitle: "Citatiestijlen voor {field}",
    fieldOgSubtitle: "Vaak vereist: {styles}.",
    fieldOgKind: "Op discipline",
    fieldKeywordStyle: "{field}-citatiestijl",
    fieldKeywordReferencing: "{field}-bronvermelding",
    fieldKeywordBest: "beste citatiestijl voor {fieldLower}",
    fieldKeywordBibliography: "literatuurlijstformaat voor {fieldLower}",
    fieldKeywordDiscipline: "citatiestijlen per discipline",
    fieldBackLink: "← Alle citatiestijlen",
    fieldHeading: "Citatiestijlen voor {field}",
    fieldRecommendedHeading: "Aanbevolen citatiestijlen",
    fieldRecommendedIntro:
      "De volgende citatiestijlen worden veel gebruikt in onderzoek, tijdschriften en cursussen in {field}. Kies een stijl voor meer informatie en om citaten te genereren.",
    fieldStyleFallback:
      "Een citatiestijl die wordt gebruikt in {field} en verwante disciplines.",
    fieldRelatedHeading: "Verwante vakgebieden",
    fieldRelatedIntro:
      "Ontdek citatiestijlen die worden gebruikt in verwante academische disciplines:",
    fieldCtaHeading: "Ontdek alle citatiestijlen",
    fieldCtaBody:
      "CitationEasy ondersteunt {count} citatiestijlen uit elk academisch vakgebied. Blader door de volledige verzameling om precies het format te vinden dat je nodig hebt.",
    fieldCtaButton: "Blader door alle stijlen →",
    fieldFaqWhichQ: "Welke citatiestijl wordt gebruikt in {field}?",
    fieldFaqWhichA:
      "Welke citatiestijl in {field} wordt gebruikt, hangt af van het specifieke tijdschrift, de instelling of de eisen van je docent. Gangbare stijlen zijn onder andere {styles}. Controleer altijd de richtlijnen van je opdracht of het doeltijdschrift voor het vereiste format.",
    fieldFaqSameQ: "Gebruiken alle tijdschriften in {field} dezelfde stijl?",
    fieldFaqSameA:
      "Nee, niet alle tijdschriften in {field} gebruiken dezelfde citatiestijl. Bepaalde stijlen komen weliswaar vaker voor in dit vakgebied, maar individuele tijdschriften kunnen hun eigen opmaakeisen hebben. Controleer altijd de inzendrichtlijnen van het tijdschrift voordat je je manuscript voorbereidt.",
    fieldFaqChooseQ:
      "Hoe kies ik een citatiestijl voor mijn werkstuk in {field}?",
    fieldFaqChooseA:
      "Om een citatiestijl te kiezen voor je werkstuk in {field}, controleer je eerst of je docent of het doeltijdschrift een bepaalde stijl voorschrijft. Is dat niet het geval, gebruik dan een van de hierboven aanbevolen stijlen voor {field}. CitationEasy ondersteunt meer dan {count} stijlen en kan gratis citaten genereren in elk daarvan.",

    // ---- /citation-styles/country/<slug> ----
    countryNotFoundTitle: "Land niet gevonden",
    countryMetaTitle: "Citatiestijlen uit {country} - APA, MLA en meer",
    countryMetaDescription:
      "Bekijk citatiestijlen die worden gebruikt in {country}. Gratis generator voor formaten uit {country}.",
    countryOgTitle: "Citatiestijlen die worden gebruikt in {country}",
    countryOgKind: "Op land",
    countryHeading: "Citatiestijlen uit {country}",
    countryIntroOne:
      "{count} citatiestijl wordt vaak gebruikt in of in verband met {country}. Kies hem om een eigen generator te openen.",
    countryIntroMany:
      "{count} citatiestijlen worden vaak gebruikt in of in verband met {country}. Kies er een om een eigen generator te openen.",
    countryBrowserTitle: "Citatiestijlen uit {country}",

    // ---- /citation-styles/type/<slug> ----
    typeNotFoundTitle: "Vakgebied niet gevonden",
    typeMetaTitle: "Citatiestijlen voor {type} - Gratis generator",
    typeMetaDescription:
      "Citatiestijlen voor {typeLower}. APA, MLA, Vancouver en meer. Gratis citatiegenerator.",
    typeOgTitle: "Citatiestijlen voor {type}",
    typeOgKind: "Op vakgebied",
    typeHeading: "Citatiestijlen voor {type}",
    typeIntroOne:
      "{count} citatiestijl voor {typeLower}. Kies hem om een eigen generator met veelgestelde vragen te openen.",
    typeIntroMany:
      "{count} citatiestijlen voor {typeLower}. Kies er een om een eigen generator met veelgestelde vragen te openen.",
    typeBrowserTitle: "Citatiestijlen voor {type}",

    // ---- Shared chrome ----
    breadcrumbStyles: "Citatiestijlen",
    // Title case, because the BreadcrumbList markup says "Citation Styles" while the
    // visible trail says "Citation styles". Two keys rather than one so translating
    // either does not silently change the other.
    breadcrumbStylesSchema: "Citatiestijlen",
    breadcrumbByCountry: "Op land",
    breadcrumbByDiscipline: "Op vakgebied",
    allStylesLink: "← Alle citatiestijlen",
    internationalLabel: "Internationaal",
  },

  // The 32 academic fields (`ACADEMIC_FIELDS` in src/data/style-pages.ts), keyed by
  // slug. Their one-sentence `description` is next door in `academicFieldDescriptions`.
  academicFields: {
    psychology: "Psychologie",
    education: "Pedagogiek",
    sociology: "Sociologie",
    anthropology: "Antropologie",
    politicalScience: "Politicologie",
    economics: "Economie",
    business: "Bedrijfskunde & management",
    literature: "Letterkunde & talen",
    history: "Geschiedenis",
    philosophy: "Filosofie",
    law: "Recht & rechtswetenschappen",
    medicine: "Geneeskunde & klinische wetenschappen",
    nursing: "Verpleegkunde & verloskunde",
    publicHealth: "Volksgezondheid & epidemiologie",
    pharmacy: "Farmacie",
    biology: "Biologie & levenswetenschappen",
    chemistry: "Scheikunde",
    physics: "Natuurkunde",
    engineering: "Techniek",
    computerScience: "Informatica",
    mathematics: "Wiskunde & statistiek",
    environmentalScience: "Milieuwetenschappen",
    earthScience: "Aard- & geowetenschappen",
    artHistory: "Kunstgeschiedenis & beeldende kunst",
    music: "Muziek & muziekwetenschap",
    theology: "Theologie & religiewetenschap",
    socialWork: "Maatschappelijk werk",
    linguistics: "Taalkunde",
    architecture: "Architectuur & stedenbouw",
    filmMedia: "Film- & mediastudies",
    journalism: "Journalistiek & communicatie",
    criminology: "Criminologie & strafrechtspleging",
  },

  // The same 32 fields' one-sentence ledes — the opening paragraph of every
  // `/citation-styles/field/<slug>` page.
  //
  // Unlike everything above, these are hand-written per field rather than mail-merged
  // from a template, which is why the extraction pass left them in
  // `src/data/style-pages.ts`: translating them is a content job, not a string swap.
  // But "hand-written" describes how they were produced, not where they belong. With
  // the chrome translated and the lede still English, the page is the hybrid CLAUDE.md
  // calls worse than an honest redirect — translated furniture around an English core.
  // `academicFieldDescription()` (src/lib/i18n/data-labels.ts) falls back to the data
  // file's own value, so a field added upstream is untranslated rather than blank.
  academicFieldDescriptions: {
    psychology:
      "Citatiestijlen die veel worden gebruikt in de psychologie, gedragswetenschappen en cognitieonderzoek.",
    education:
      "Citatiestijlen voor onderwijsonderzoek, pedagogiek en curriculumstudies.",
    sociology:
      "Citatiestijlen die worden gebruikt in sociologisch onderzoek en de sociale theorie.",
    anthropology:
      "Citatiestijlen voor antropologisch en etnografisch onderzoek.",
    politicalScience:
      "Citatiestijlen voor politicologie, bestuurskunde en onderzoek naar overheidsbeleid.",
    economics:
      "Citatiestijlen die worden gebruikt in economisch en econometrisch onderzoek.",
    business:
      "Citatiestijlen voor onderzoek op het gebied van bedrijfskunde, management, marketing en finance.",
    literature: "Citatiestijlen voor letterkunde, taalkunde en taalonderzoek.",
    history:
      "Citatiestijlen voor historisch onderzoek, historiografie en archiefstudies.",
    philosophy: "Citatiestijlen voor filosofische teksten en ethiekonderzoek.",
    law: "Juridische citatiestijlen voor gerechtsdocumenten, rechtstijdschriften en juridische vakliteratuur.",
    medicine:
      "Citatiestijlen voor medisch onderzoek, klinische studies en de gezondheidszorg.",
    nursing:
      "Citatiestijlen voor verplegingsonderzoek en de praktijk in de gezondheidszorg.",
    publicHealth:
      "Citatiestijlen voor onderzoek naar volksgezondheid en epidemiologische studies.",
    pharmacy: "Citatiestijlen voor farmaceutisch en farmacologisch onderzoek.",
    biology: "Citatiestijlen voor biologisch onderzoek, ecologie en genetica.",
    chemistry:
      "Citatiestijlen voor scheikunde, biochemie en materiaalkundig onderzoek.",
    physics:
      "Citatiestijlen voor natuurkunde, astrofysica en toegepast natuurkundig onderzoek.",
    engineering:
      "Citatiestijlen voor techniek en toegepast technologisch onderzoek.",
    computerScience:
      "Citatiestijlen voor informatica, AI-onderzoek en software engineering.",
    mathematics:
      "Citatiestijlen voor wiskunde, toegepaste wiskunde en statistisch onderzoek.",
    environmentalScience:
      "Citatiestijlen voor milieuonderzoek en duurzaamheidsstudies.",
    earthScience:
      "Citatiestijlen voor geologie, geofysica en aardwetenschappelijk onderzoek.",
    artHistory:
      "Citatiestijlen voor kunstgeschiedenis, visuele cultuur en museumstudies.",
    music:
      "Citatiestijlen voor muziekonderzoek, muziektheorie en uitvoeringspraktijk.",
    theology:
      "Citatiestijlen voor theologisch en religiewetenschappelijk onderzoek.",
    socialWork:
      "Citatiestijlen voor onderzoek en praktijk in het maatschappelijk werk.",
    linguistics: "Citatiestijlen voor taalkunde, fonetiek en taalwetenschap.",
    architecture:
      "Citatiestijlen voor architectuur, stedenbouw en planologisch onderzoek.",
    filmMedia:
      "Citatiestijlen voor film, televisie, mediastudies en communicatiewetenschap.",
    journalism:
      "Citatiestijlen voor journalistiek, mediastudies en communicatieonderzoek.",
    criminology:
      "Citatiestijlen voor criminologie en onderzoek naar strafrechtspleging.",
  },

  // The 68 country/region facets (`REGIONS` in src/data/citation-styles.ts), keyed by
  // slug. `getRegionCountryCode` still keys off the English data value, so translating these
  // moves the label without moving the flag.
  regions: {
    unitedStates: "Verenigde Staten",
    unitedKingdom: "Verenigd Koninkrijk",
    australia: "Australië",
    canada: "Canada",
    newZealand: "Nieuw-Zeeland",
    ireland: "Ierland",
    southAfrica: "Zuid-Afrika",
    germany: "Duitsland",
    france: "Frankrijk",
    spain: "Spanje",
    italy: "Italië",
    netherlands: "Nederland",
    belgium: "België",
    switzerland: "Zwitserland",
    austria: "Oostenrijk",
    portugal: "Portugal",
    luxembourg: "Luxemburg",
    sweden: "Zweden",
    norway: "Noorwegen",
    denmark: "Denemarken",
    finland: "Finland",
    iceland: "IJsland",
    poland: "Polen",
    czechRepublic: "Tsjechië",
    hungary: "Hongarije",
    romania: "Roemenië",
    russia: "Rusland",
    ukraine: "Oekraïne",
    greece: "Griekenland",
    turkey: "Turkije",
    croatia: "Kroatië",
    slovenia: "Slovenië",
    slovakia: "Slowakije",
    serbia: "Servië",
    bulgaria: "Bulgarije",
    lithuania: "Litouwen",
    latvia: "Letland",
    estonia: "Estland",
    japan: "Japan",
    china: "China",
    southKorea: "Zuid-Korea",
    india: "India",
    singapore: "Singapore",
    hongKong: "Hongkong",
    taiwan: "Taiwan",
    malaysia: "Maleisië",
    indonesia: "Indonesië",
    thailand: "Thailand",
    vietnam: "Vietnam",
    philippines: "Filipijnen",
    pakistan: "Pakistan",
    bangladesh: "Bangladesh",
    sriLanka: "Sri Lanka",
    israel: "Israël",
    saudiArabia: "Saoedi-Arabië",
    unitedArabEmirates: "Verenigde Arabische Emiraten",
    brazil: "Brazilië",
    mexico: "Mexico",
    argentina: "Argentinië",
    chile: "Chili",
    colombia: "Colombia",
    peru: "Peru",
    venezuela: "Venezuela",
    egypt: "Egypte",
    nigeria: "Nigeria",
    kenya: "Kenia",
    morocco: "Marokko",
    international: "Internationaal",
  },

  // The 39 subject categories (`CATEGORIES` in src/data/citation-styles.ts), keyed by
  // slug. The emoji beside each one stays in the data — it is language-neutral.
  styleCategories: {
    medicine: "Geneeskunde",
    nursing: "Verpleegkunde",
    publicHealth: "Volksgezondheid",
    pharmacy: "Farmacie",
    dentistry: "Tandheelkunde",
    veterinary: "Diergeneeskunde",
    psychology: "Psychologie",
    sociology: "Sociologie",
    anthropology: "Antropologie",
    politicalScience: "Politicologie",
    economics: "Economie",
    education: "Pedagogiek",
    socialWork: "Maatschappelijk werk",
    biology: "Biologie",
    chemistry: "Scheikunde",
    physics: "Natuurkunde",
    environmentalScience: "Milieuwetenschappen",
    earthScience: "Aardwetenschappen & geologie",
    agriculture: "Landbouwwetenschappen",
    ecology: "Ecologie",
    engineering: "Techniek",
    computerScience: "Informatica",
    mathematics: "Wiskunde",
    statistics: "Statistiek",
    history: "Geschiedenis",
    literature: "Letterkunde",
    philosophy: "Filosofie",
    linguistics: "Taalkunde",
    artHistory: "Kunstgeschiedenis",
    music: "Muziek",
    filmMedia: "Film & media",
    archaeology: "Archeologie",
    law: "Recht",
    business: "Bedrijfskunde",
    architecture: "Architectuur",
    religion: "Religie & theologie",
    geography: "Geografie",
    general: "Algemeen / multidisciplinair",
    other: "Overig",
  },

  // The closed vocabularies `STYLE_LANDING_PAGES` (src/data/style-pages.ts) draws on for
  // the 30 major styles: what each style calls its end-of-paper list, the shape of its
  // in-text citation, its edition, and the disciplines that require it. The comparison
  // pages print all four in a table and the field pages print two of them under every
  // style card, so leaving them in the data means a translated page with an English
  // table in the middle of it.
  //
  // Style NAMES (`name`, `fullName`) are NOT here: a style's official name is what it
  // is called everywhere. The 30 hand-written taglines are, in `styleTaglines` below.
  styleFacts: {
    // `referenceListName` — 6 distinct values across the 30 styles.
    listReferenceList: "Literatuurlijst",
    listWorksCited: "Geciteerde werken",
    listReferences: "Bronnenlijst",
    listBibliography: "Bibliografie",
    listTableOfAuthorities: "Overzicht van rechtsbronnen",
    listReferencesCited: "Geciteerde literatuur",
    listLowerReferenceList: "literatuurlijst",
    listLowerWorksCited: "geciteerde werken",
    listLowerReferences: "bronnenlijst",
    listLowerBibliography: "bibliografie",
    listLowerTableOfAuthorities: "overzicht van rechtsbronnen",
    listLowerReferencesCited: "geciteerde literatuur",

    // `inTextFormat` — 14 distinct values. "Author", "Year" and "Page" are slot names,
    // not part of the citation, so they read as English words on a Spanish page.
    inTextAuthorCommaYear: "(Auteur, Jaar)",
    inTextAuthorPage: "(Auteur Pagina)",
    inTextAuthorYear: "(Auteur Jaar)",
    inTextSuperscriptOrBracketed:
      "Superscript- of tussen haakjes geplaatste cijfers (1)",
    inTextBracketedNumbers: "Cijfers tussen haakjes [1]",
    inTextSuperscriptNumbers: "Superscriptcijfers¹",
    inTextSuperscriptOrAuthorYear: "Superscriptcijfers of (Auteur Jaar)",
    inTextKeyBased: "Verwijzingen op basis van sleutels",
    inTextSuperscriptOrAuthorCommaYear: "Superscriptcijfers of (Auteur, Jaar)",
    inTextFootnotesCaseLegislation:
      "Voetnoten met verwijzingen naar rechtspraak en wetgeving",
    inTextFootnotesSignals: "Voetnoten met signaalafkortingen",
    inTextFootnotesPinpoint: "Voetnoten met exacte vindplaatsen",
    inTextParenNumbers: "Cijfers tussen haakjes (1)",
    inTextItalicNumbers: "Cursieve cijfers (1)",

    // `edition` — 12 distinct values. The ordinal and the word "edition" translate; the
    // handbook name inside "Cite Them Right 11th edition" does not.
    edition4th: "4e editie",
    edition5th: "5e editie",
    edition7th: "7e editie",
    edition8th: "8e editie",
    edition9th: "9e editie",
    edition11th: "11e editie",
    edition18th: "18e editie",
    edition22nd: "22e editie",
    editionCiteThemRight11th: "Cite Them Right 11e editie",
    editionIcmjeStandard: "ICMJE-standaard",
    editionStandard: "standaard",
    editionCurrent: "actueel",

    // `fields` — the 67 discipline names the 30 styles are tagged with.
    fieldAcoustics: "Akoestiek",
    fieldAnthropology: "Antropologie",
    fieldAppliedPhysics: "Toegepaste natuurkunde",
    fieldArchaeology: "Archeologie",
    fieldArts: "Kunst",
    fieldAstrophysics: "Astrofysica",
    fieldAustralianLaw: "Australisch recht",
    fieldBiochemistry: "Biochemie",
    fieldBiology: "Biologie",
    fieldBiomedicalSciences: "Biomedische wetenschappen",
    fieldBiotechnology: "Biotechnologie",
    fieldBusiness: "Bedrijfskunde",
    fieldCellBiology: "Celbiologie",
    fieldChemicalEngineering: "Chemische technologie",
    fieldChemistry: "Scheikunde",
    fieldClinicalResearch: "Klinisch onderzoek",
    fieldComputerScience: "Informatica",
    fieldCondensedMatter: "Fysica van de gecondenseerde materie",
    fieldCourtDocuments: "Gerechtelijke documenten",
    fieldCriminology: "Criminologie",
    fieldCulturalStudies: "Cultuurwetenschappen",
    fieldDemography: "Demografie",
    fieldEcology: "Ecologie",
    fieldEducation: "Pedagogiek",
    fieldElectronics: "Elektronica",
    fieldEngineering: "Techniek",
    fieldEnvironmentalScience: "Milieuwetenschappen",
    fieldEpidemiology: "Epidemiologie",
    fieldEthnography: "Etnografie",
    fieldGenetics: "Genetica",
    fieldGeology: "Geologie",
    fieldGlobalHealth: "Wereldwijde gezondheid",
    fieldHealthSciences: "Gezondheidswetenschappen",
    fieldHistory: "Geschiedenis",
    fieldHumanities: "Geesteswetenschappen",
    fieldInformationSystems: "Informatiesystemen",
    fieldInformationTechnology: "Informatietechnologie",
    fieldInternationalRelations: "Internationale betrekkingen",
    fieldJurisprudence: "Rechtswetenschap",
    fieldLanguages: "Talen",
    fieldLaw: "Recht",
    fieldLawReview: "Rechtstijdschrift",
    fieldLegalStudies: "Rechtsgeleerdheid",
    fieldLifeSciences: "Levenswetenschappen",
    fieldLiterature: "Letterkunde",
    fieldMaterialsScience: "Materiaalkunde",
    fieldMathematics: "Wiskunde",
    fieldMedicine: "Geneeskunde",
    fieldMolecularBiology: "Moleculaire biologie",
    fieldMultidisciplinary: "Multidisciplinair",
    fieldMultidisciplinaryScience: "Multidisciplinaire wetenschap",
    fieldNaturalSciences: "Natuurwetenschappen",
    fieldNursing: "Verpleegkunde",
    fieldPharmacy: "Farmacie",
    fieldPhysics: "Natuurkunde",
    fieldPoliticalScience: "Politicologie",
    fieldPsychology: "Psychologie",
    fieldPublicHealth: "Volksgezondheid",
    fieldPublicPolicy: "Overheidsbeleid",
    fieldPublishing: "Uitgeverij",
    fieldScience: "Wetenschap",
    fieldSciences: "Wetenschappen",
    fieldSocialSciences: "Sociale wetenschappen",
    fieldSocialWork: "Maatschappelijk werk",
    fieldSociology: "Sociologie",
    fieldSoftwareEngineering: "Software-engineering",
    fieldTheology: "Theologie",
  },

  // The 30 major styles' hero taglines (`STYLE_LANDING_PAGES[].tagline`), keyed by
  // slug.
  //
  // Per-page hand-written copy, not a template — which is why they outlived the
  // extraction pass in `src/data/style-pages.ts`. Three page types print them: the
  // style landing page's hero, the card for each recommended style on a field
  // taxonomy page, and the lower-cased clause inside a comparison page's opening
  // sentence ("APA (…) is the most widely used citation style in the social
  // sciences"). Leaving them in the data means an English sentence in the first
  // paragraph of an otherwise translated page. `styleTagline()`
  // (src/lib/i18n/data-labels.ts) falls back to the data file's own value.
  styleTaglines: {
    apa: "De meest gebruikte citatiestijl in de sociale wetenschappen",
    mla: "De standaardcitatiestijl voor de geesteswetenschappen",
    chicago:
      "Amerika's meest vertrouwde stijlgids voor auteurs, redacteuren en uitgevers",
    harvard:
      "De populairste referentiestijl aan Britse en Australische universiteiten",
    vancouver:
      "De standaardcitatiestijl voor medische en biomedische tijdschriften",
    ieee: "De veelgebruikte citatiestijl voor techniek en informatica",
    ama: "De citatiestandaard voor Amerikaanse medische tijdschriften",
    turabian: "De studentvriendelijke versie van de Chicago-stijl",
    cse: "De voorkeurscitatiestijl voor de natuur- en scheikundige wetenschappen",
    bibtex:
      "Het literatuurlijstformaat voor LaTeX-gebruikers en academische opmaak",
    nature:
      "De citatiestijl van 's werelds toonaangevende multidisciplinaire tijdschrift",
    acs: "De citatiestandaard voor scheikunde en de chemische wetenschappen",
    oscola:
      "De standaard juridische citatiestijl voor Britse rechtenfaculteiten en tijdschriften",
    bluebook:
      "Het gezaghebbende juridische citatiesysteem voor Amerikaanse rechtbanken en rechtstijdschriften",
    aglc: "De standaard juridische citatiestijl voor Australische rechtenfaculteiten en rechtbanken",
    asa: "De standaardcitatiestijl voor sociologie en sociaal onderzoek",
    apsa: "De citatiestijl voor politicologisch onderzoek en tijdschriften",
    aaa: "De standaardcitatiestijl voor antropologisch onderzoek",
    nlm: "De citatiestandaard van PubMed en de Amerikaanse National Library of Medicine",
    acm: "De citatiestijl voor ACM-conferenties en informaticaonderzoek",
    aps: "De citatiestijl voor Physical Review en APS-tijdschriften",
    aip: "De citatiestijl voor AIP- en natuurkundige tijdschriften",
    cell: "De citatiestijl voor Cell Press en toonaangevende biologische tijdschriften",
    science:
      "De citatiestijl voor het tijdschrift Science en AAAS-tijdschriften",
    plos: "De citatiestijl voor open-access-tijdschriften van PLOS",
    bmj: "De citatiestijl voor BMJ en toonaangevende Britse medische tijdschriften",
    lancet: "De citatiestijl voor de tijdschriftenfamilie van The Lancet",
    jama: "De citatiestijl voor JAMA en de tijdschriften van het JAMA Network",
    elsevierHarvard:
      "De Harvard-stijlvariant die in tijdschriften van Elsevier wordt gebruikt",
    springer:
      "De citatiestijl voor tijdschriften en boeken van Springer Nature",
  },

  // The 105 style-versus-style pages at `/<locale>/compare/<slug>`.
  //
  // The two style names arrive as placeholder values and are never translated. The
  // facts in the table come from `styleFacts` above and the taglines from
  // `styleTaglines`; only `fullName` stays in `src/data/style-pages.ts`, because an
  // official name is the same in every language.
  comparePage: {
    notFoundTitle: "Niet gevonden",
    metaTitle: "{a} vs {b} - De belangrijkste verschillen uitgelegd [2026]",
    metaDescription:
      "Vergelijk de citatiestijlen {a} en {b} naast elkaar. Ontdek de belangrijkste verschillen in opmaak, verwijzingen in de tekst en literatuurlijsten om de juiste stijl voor je werkstuk te kiezen.",
    ogTitle: "{a} vs {b}",
    ogSubtitle:
      "Vorm in de tekst, vermeldingen in de literatuurlijst, hoofdlettergebruik en welke disciplines welke stijl vereisen.",
    ogKind: "Stijlvergelijking",
    keywordVs: "{a} vs {b}",
    keywordVersus: "{a} versus {b}",
    keywordDifference: "verschil tussen {a} en {b}",
    keywordOr: "{a} of {b}",
    keywordComparison: "vergelijking van citatiestijlen",
    breadcrumbCompare: "Stijlen vergelijken",
    breadcrumbCurrent: "{a} vs {b}",
    backLink: "← Terug naar de vergelijkingstool",
    heading: "{a} vs {b}: de belangrijkste verschillen",
    intro:
      "{a} en {b} behoren tot de meest gebruikte citatiestijlen in de academische wereld, maar ze verschillen op belangrijke punten. {introA} {introB} Als je de verschillen kent, kun je makkelijker het juiste format voor je werkstuk kiezen.",
    styleIntro: "{style} ({fullName}) {tagline}.",
    styleIntroFallback:
      "{style} is een populaire citatiestijl die in veel academische disciplines wordt gebruikt.",
    tableHeading: "Vergelijking naast elkaar",
    tableFeature: "Kenmerk",
    tableCitationSystem: "Verwijssysteem",
    tableInText: "Vorm in de tekst",
    tableReferenceList: "Naam van de lijst",
    tableEdition: "Editie",
    tableCommonFields: "Gebruikelijke vakgebieden",
    // Title case, for the table cell. `styleLanding.system*` says "Author–date" for the
    // fact list on the style landing pages; both spellings are deliberate.
    systemAuthorDate: "Auteur-jaar",
    systemNumbered: "Genummerd",
    systemFootnotes: "Voetnoten",
    // The same three systems inside a sentence ("uses a numbered citation system").
    describeAuthorDate: "op auteur en jaar gebaseerd",
    describeNumbered: "genummerd",
    describeFootnotes: "op voetnoten gebaseerd",
    whenToUseHeading: "Wanneer gebruik je {style}",
    whenToUseBody:
      "{style} ({fullName}) gebruikt een {system} citatiesysteem met verwijzingen in de tekst in de vorm {inText}, en vermeldt de bronnen aan het einde van het werkstuk onder de kop {list}. Deze stijl wordt het vaakst gebruikt in {fields}{more}.",
    whenToUseMoreFields: " en verwante vakgebieden",
    whenToUseFallback:
      "{style} is een algemeen erkende citatiestijl. Controleer de richtlijnen van je opdracht of het doeltijdschrift om te bevestigen of {style} het vereiste format is.",
    ctaHeading: "Bekijk het verschil in de praktijk",
    ctaBodyBefore: "Probeer onze",
    ctaBodyLink: "interactieve vergelijkingstool",
    ctaBodyAfter:
      "en vergelijk {a}- en {b}-citaten naast elkaar met je eigen bronnen.",
    ctaCiteIn: "Citeren in {style} →",
    faqDifferenceQ: "Wat is het verschil tussen {a} en {b}?",
    faqDifferenceA:
      "{a} en {b} verschillen in de vorm van de verwijzing in de tekst, de opbouw van de literatuurlijst en de academische disciplines waarin ze worden gebruikt. {detail}",
    faqDifferenceDetail:
      "{a} gebruikt {inTextA} als verwijzing in de tekst, met als kop {listA}; {b} gebruikt {inTextB}, met als kop {listB}.",
    faqDifferenceDetailFallback:
      "Elke stijl heeft eigen regels voor het opmaken van citaten, het opbouwen van literatuurlijsten en het omgaan met verschillende brontypen.",
    faqBetterQ: "Wat is beter, {a} of {b}?",
    faqBetterA:
      "Noch {a}, noch {b} is op zichzelf beter. De juiste keuze hangt af van je vakgebied, de eisen van je docent of het tijdschrift waarbij je indient. Controleer de richtlijnen van je opdracht of doelpublicatie om te bepalen welke stijl je gebruikt.",
    faqSwitchQ: "Kan ik wisselen tussen {a} en {b}?",
    faqSwitchA:
      "Ja, met de gratis generator van CitationEasy kun je wisselen tussen {a} en {b}. Selecteer gewoon de gewenste stijl en je citaten worden automatisch opnieuw opgemaakt. Gebruik per werkstuk wel maar één stijl, tenzij je docent iets anders aangeeft.",
  },

  // The ~21 converter-pair pages at `/<locale>/convert/<slug>`.
  //
  // Only the shell is here; each pair's own four strings are in `converterCopy` below.
  // The format names (DOI, ISBN, BibTeX, RIS, CSL-JSON, PubMed ID, arXiv ID, Word) are
  // identifiers, never translated.
  converterPage: {
    notFoundTitle: "Converter niet gevonden",
    metaTitle: "{title} - Gratis & direct",
    ogTitle: "{title}",
    ogKind: "Converter",
    breadcrumbConverters: "Converters",
    breadcrumbLabel: "Kruimelpad",
    whenHeading: "Wanneer je dit nodig hebt",
    relatedHeading: "Vergelijkbare converters",
    allConverters: "Alle converters",
    formatConverterTool: "Citatieformaat-converter",
    faqHowQ: "Hoe converteer ik {input} naar {output}?",
    faqHowA:
      "{intro} Alles is gratis, zonder account en zonder conversielimiet.",
    faqPrivacyQ: "Worden mijn gegevens ergens geüpload?",
    faqPrivacyIdentifier:
      "Elke {input} wordt opgezocht bij de betreffende openbare metadatadienst (Crossref voor DOI's, PubMed voor PMID's, bibliotheekcatalogi voor ISBN's), waarbij alleen de identifier zelf wordt verzonden — nooit je documenten. De opgemaakte resultaten zijn van jou om te kopiëren of te downloaden; er wordt niets opgeslagen.",
    faqPrivacyLocal:
      "Nee — de {input} die je plakt of uploadt, wordt volledig in je browser verwerkt. {detail}",
    faqPrivacyDetailStyle:
      "Alleen de uitgelezen velden (auteur, titel, jaar) worden naar onze opmaakservice gestuurd om de citaten te genereren; je bestand zelf verlaat nooit je apparaat.",
    faqPrivacyDetailOffline:
      "De conversie verloopt volledig offline op de pagina — er wordt niets naar een server gestuurd.",
    faqUsefulQ: "Wanneer is een conversie van {input} naar {output} nuttig?",
    faqOtherQ: "Kan ik ook naar andere stijlen en formaten converteren?",
    faqOtherA:
      "Ja. De generator van CitationEasy maakt elke bron op in {count} citatiestijlen, de literatuurlijst exporteert naar Word, RTF, BibTeX, RIS en CSL-JSON, en het overzicht met converters bevat elk direct conversiepaar. Heb je al opgemaakte tekst die je van de ene stijl naar de andere wilt herschrijven, gebruik dan de tool Citatieformaat-converter.",
  },

  // The 21 converter pairs' own copy (`CONVERTER_PAIRS` in src/data/converters.ts),
  // keyed by slug: title, meta description, the intro paragraph under the H1, and the
  // "when you need this" paragraph.
  //
  // Four hand-written strings per pair, and `src/data/converters.ts` has a quality rule
  // saying they must stay hand-written — no mail-merged sentences, because mass-templated
  // pages are what earned the site a scaled-content penalty. That rule is about how the
  // copy is authored; it says nothing about which language a reader gets it in. Left in
  // the data file they are the entire body of an otherwise translated page.
  // `converterPairCopy()` (src/lib/i18n/data-labels.ts) returns all four at once and
  // falls back to the data file per field, so a converter added upstream ships English
  // copy rather than a blank page.
  converterCopy: {
    doiToApaTitle: "DOI-naar-APA-citatieconverter",
    doiToApaMetaDescription:
      "Plak DOI's (één per regel) en krijg direct APA-7-referenties, gehaald uit de officiële Crossref-metadata van de uitgever. Gratis, zonder account.",
    doiToApaIntro:
      "Een DOI verwijst al naar de gezaghebbende metadata van de uitgever, dus je hoeft geen APA-referentie met de hand te typen. Plak één DOI per regel en elke regel wordt omgezet in een volledige APA-7-referentie — auteurs omgekeerd, titel in zinsvorm, cursief gezette tijdschrifttitel en de https://doi.org/-link die APA vereist.",
    doiToApaUseCase:
      "Herkenbaar moment: je docent wil een APA-literatuurlijst, en je hebt alleen de stapel DOI's die je tijdens het lezen hebt bewaard. Plak de stapel, kopieer de lijst.",
    doiToMlaTitle: "DOI-naar-MLA-citatieconverter",
    doiToMlaMetaDescription:
      "Zet DOI's automatisch om in MLA-9-vermeldingen voor de literatuurlijst, met Crossref-metadata. Plak één DOI per regel — gratis, niets te installeren.",
    doiToMlaIntro:
      "MLA 9 behandelt een tijdschriftartikel als een keten van containers — artikeltitel tussen aanhalingstekens, tijdschrift cursief, jaargang, nummer, jaar, pagina's — en dat is lastig om met de hand samen te stellen. Een DOI-opzoeking vult elk veld in vanuit het uitgeversrecord, waardoor de vermelding meteen goed staat.",
    doiToMlaUseCase:
      "Handig wanneer een opleiding letterkunde MLA vereist, maar je bronnen uit bètagerichte databases komen die alleen DOI's opleveren.",
    doiToBibtexTitle: "DOI-naar-BibTeX-converter",
    doiToBibtexMetaDescription:
      "Zet DOI's om in BibTeX-vermeldingen voor LaTeX en Overleaf. Plak één DOI per regel, download een kant-en-klaar .bib-bestand — gratis en onbeperkt.",
    doiToBibtexIntro:
      "Elk LaTeX-document heeft uiteindelijk een .bib-bestand nodig. Plak DOI's per regel en krijg nette @article-vermeldingen met gegenereerde citeersleutels, klaar voor Overleaf, natbib of biblatex — geen velden meer met de hand overtypen van uitgeverspagina's.",
    doiToBibtexUseCase:
      "Het klassieke scenario: een reviewer stuurt je drie te citeren artikelen, jij hebt hun DOI's, en je deadline is vanavond. Plakken, downloaden, \\cite gebruiken.",
    doiToRisTitle: "DOI-naar-RIS-converter",
    doiToRisMetaDescription:
      "Zet DOI's om in een RIS-bestand dat Zotero, Mendeley en EndNote direct importeren. Plak DOI's per regel — gratis, geen registratie.",
    doiToRisIntro:
      "RIS is het uitwisselingsformaat dat elke literatuurbeheerder importeert. Plak DOI's en download een .ris-bestand waarvan de velden TY/AU/TI/T2 rechtstreeks van Crossref komen — importeer het vervolgens via Bestand → Importeren in Zotero, Mendeley of EndNote, en de vermeldingen komen volledig gestructureerd binnen.",
    doiToRisUseCase:
      "Ideaal om een handmatig verzamelde DOI-lijst over te zetten naar een echte literatuurbeheerder, zonder ook maar één auteursnaam opnieuw te typen.",
    isbnToApaTitle: "ISBN-naar-APA-citatieconverter",
    isbnToApaMetaDescription:
      "Zet ISBN's automatisch om in APA-7-boekreferenties. Plak één ISBN per regel en kopieer de opgemaakte lijst — gratis, zonder account.",
    isbnToApaIntro:
      "De ISBN op de colofonpagina van een boek is alles wat je nodig hebt voor de APA-referentie. Elke ISBN wordt opgezocht in bibliotheekmetadata en opgemaakt in APA-7-stijl — auteur, jaar, cursief gezette titel en uitgever — inclusief druk- of editie-informatie als het record die bevat.",
    isbnToApaUseCase:
      "De snelste manier om een stapel fysieke bibliotheekboeken te citeren: sla bij elk boek de colofonpagina op, typ de ISBN's, plak de lijst hier.",
    isbnToMlaTitle: "ISBN-naar-MLA-citatieconverter",
    isbnToMlaMetaDescription:
      "Zet ISBN's in één keer om in MLA-9-vermeldingen voor de literatuurlijst. Gratis ISBN-opzoeking met opgemaakte uitvoer — geen registratie nodig.",
    isbnToMlaIntro:
      "MLA-9-boekvermeldingen zijn kort — auteur, cursief gezette titel, uitgever, jaar — maar de juiste omkering van de auteursnaam en interpunctie blijft lastig. Een ISBN-opzoeking haalt het echte catalogusrecord van het boek op en zet het om volgens de kernelementensjabloon van de MLA-handleiding.",
    isbnToMlaUseCase:
      "Ideaal voor letterkunde-essays: citeer elke roman en elke kritische monografie op je bureau door alleen de nummers op de achterflap over te typen.",
    isbnToBibtexTitle: "ISBN-naar-BibTeX-converter",
    isbnToBibtexMetaDescription:
      "Zet ISBN's om in BibTeX-@book-vermeldingen voor LaTeX. Plak één ISBN per regel, download het .bib-bestand — gratis en direct.",
    isbnToBibtexIntro:
      "Boeken zijn de lastige hoek van BibTeX — geen DOI om op te zoeken, en uitgeverspagina's bieden zelden een .bib-export. Deze converter koppelt elke ISBN aan bibliotheekmetadata en genereert @book-vermeldingen met ingevulde velden voor auteur, titel, uitgever en jaar.",
    isbnToBibtexUseCase:
      "Voor scriptieschrijvers: de methodehoofdstukken citeren DOI's van tijdschriftartikelen, maar het theoriehoofdstuk citeert twaalf boeken. Die twaalf boeken regelt deze converter.",
    pmidToApaTitle: "PMID-naar-APA-citatieconverter",
    pmidToApaMetaDescription:
      "Zet PubMed-ID's automatisch om in APA-7-referenties. Plak PMID's per regel en krijg een opgemaakte literatuurlijst — gratis.",
    pmidToApaIntro:
      "Elk PubMed-record heeft een PMID, en opleidingen psychologie en verpleegkunde vereisen vrijwel altijd APA. Plak PMID's per regel en elk record wordt via de eigen metadata van PubMed omgezet in een volledige APA-7-tijdschriftreferentie, inclusief DOI-link als het record die heeft.",
    pmidToApaUseCase:
      "Gemaakt voor opdrachten over evidence-based practice: je zoekstrategie eindigt met een lijst PMID's, en je werkstuk heeft ze nodig in APA.",
    pmidToRisTitle: "PMID-naar-RIS-converter",
    pmidToRisMetaDescription:
      "Zet PubMed-ID's om in een RIS-bestand voor Zotero, Mendeley of EndNote. Plak PMID's per regel, download het .ris-bestand — gratis.",
    pmidToRisIntro:
      "Systematic-review-workflows draaien op RIS-bestanden. Plak de PMID's uit je PubMed-zoekopdracht en download een .ris-bestand, klaar voor je screeningtool of literatuurbeheerder — elk record gestructureerd op basis van de canonieke metadata van PubMed in plaats van gescrapete tekst.",
    pmidToRisUseCase:
      "Handig halverwege een review, wanneer een collega je vijf extra PMID's mailt met het verzoek ook die studies mee te nemen, terwijl jouw bibliotheek in EndNote staat.",
    arxivToBibtexTitle: "arXiv-naar-BibTeX-converter",
    arxivToBibtexMetaDescription:
      "Zet arXiv-ID's om in BibTeX-vermeldingen voor LaTeX-documenten. Plak ID's zoals 2301.12345, één per regel, en download het .bib-bestand — gratis.",
    arxivToBibtexIntro:
      "In snel bewegende vakgebieden worden preprints voortdurend geciteerd, en arXiv-ID's (zoals 2301.12345) zijn hoe ze circuleren. Plak de ID's en krijg BibTeX-vermeldingen met de auteurs, titel, jaar en arXiv-link van de preprint — klaar voor het .bib-bestand van je paper.",
    arxivToBibtexUseCase:
      "Voor schrijvers in ML en natuurkunde wier related-work-sectie voor 80% uit arXiv bestaat: verzamel de ID's tijdens het lezen, zet ze in één keer om tijdens het schrijven.",
    bibtexToApaTitle: "BibTeX-naar-APA-converter",
    bibtexToApaMetaDescription:
      "Plak of upload een .bib-bestand en krijg een APA-7-literatuurlijst. Verwerkt bestanden met meerdere vermeldingen — gratis, de verwerking gebeurt in je browser.",
    bibtexToApaIntro:
      "Soms verlaat het document LaTeX: een tijdschrift wil een Word-inzending, of een commissie wil de literatuurlijst als platte tekst. Plak je BibTeX (of upload het .bib-bestand) en elke vermelding wordt in je browser verwerkt en vervolgens opgemaakt als een APA-7-literatuurlijst die je in elk document kunt plakken.",
    bibtexToApaUseCase:
      "De klassieker bij subsidieaanvragen: je publicatielijst staat in een .bib-bestand, en het formulier van de financier wil APA-opgemaakte referenties in een tekstvak.",
    bibtexToMlaTitle: "BibTeX-naar-MLA-converter",
    bibtexToMlaMetaDescription:
      "Zet BibTeX om in MLA-9-vermeldingen voor de literatuurlijst. Plak of upload het .bib-bestand en kopieer de opgemaakte lijst — gratis, zonder account.",
    bibtexToMlaIntro:
      "BibTeX slaat ruwe velden op; MLA schrijft precies voor hoe je die rangschikt. Deze converter verwerkt je .bib-vermeldingen lokaal en zet elke om via de MLA-9-sjabloon — omkering van de auteursnaam, artikeltitels tussen aanhalingstekens, cursief gezette containers en correcte interpunctie tussen elementen.",
    bibtexToMlaUseCase:
      "Voor onderzoekers die van vakgebied wisselen: je referenties staan in BibTeX uit eerder LaTeX-werk, maar het geesteswetenschappelijke tijdschrift waar je bij indient, wil MLA.",
    bibtexToRisTitle: "BibTeX-naar-RIS-converter",
    bibtexToRisMetaDescription:
      "Zet een .bib-bestand om naar RIS voor Zotero, Mendeley of EndNote. Plak of upload BibTeX, download het .ris-bestand — gratis en offline.",
    bibtexToRisIntro:
      "Overstappen van LaTeX-gereedschap naar een literatuurbeheerder betekent dat je de @article/@book-vermeldingen van BibTeX vertaalt naar de TY/AU/TI-tags van RIS. De conversie gebeurt volledig in je browser — inclusief veldkoppelingen — en levert je een .ris-bestand op dat elke beheerder importeert.",
    bibtexToRisUseCase:
      "Typische aanleiding: een nieuwe co-auteur werkt met Zotero, jullie gedeelde referenties staan als .bib in de repo, en iemand moet de kloof overbruggen.",
    bibtexToCslJsonTitle: "BibTeX-naar-CSL-JSON-converter",
    bibtexToCslJsonMetaDescription:
      "Zet BibTeX om naar CSL-JSON voor Pandoc en Quarto. Plak of upload het .bib-bestand, download references.json — gratis, in de browser.",
    bibtexToCslJsonIntro:
      "Pandoc en Quarto citeren native uit CSL-JSON, en het omzetten van je bestaande .bib-bestand haalt een storingsgevoelig onderdeel uit je build. Vermeldingen worden in je browser verwerkt en komen eruit als een schone CSL-JSON-array — precies de vorm die --bibliography=references.json verwacht.",
    bibtexToCslJsonUseCase:
      "Voor Markdown-georiënteerde onderzoekers die een oud LaTeX-project omzetten naar Quarto: neem de literatuurlijst mee zonder JSON met de hand te bewerken.",
    bibtexToWordTitle: "BibTeX-naar-Word-converter",
    bibtexToWordMetaDescription:
      "Zet een .bib-bestand om in een Word-document met een APA-opgemaakte literatuurlijst met inspringing. Gratis — de verwerking gebeurt in je browser.",
    bibtexToWordIntro:
      "Wanneer een .bib-bestand een document moet worden — geen ander gegevensformaat —, verwerkt deze converter je BibTeX lokaal, maakt elke vermelding op in APA-7-stijl en downloadt een Word-bestand met een literatuurlijst die al alfabetisch is gesorteerd en voorzien is van inspringing.",
    bibtexToWordUseCase:
      "De laatste stap in het proces: het tijdschrift heeft het artikel geaccepteerd maar wil de definitieve literatuurlijst als bewerkbaar Word-bestand, terwijl jouw bron van waarheid BibTeX is.",
    risToApaTitle: "RIS-naar-APA-converter",
    risToApaMetaDescription:
      "Plak of upload een RIS-export en krijg een APA-7-literatuurlijst. Werkt met exports uit Zotero, Mendeley, EndNote en databases — gratis.",
    risToApaIntro:
      "De exportknop van elke database levert RIS op; opdrachten willen APA. Plak de export (of upload het .ris-bestand) en elk TY/AU/TI-record wordt in je browser verwerkt en weergegeven als een APA-7-referentie — geen literatuurbeheerder nodig.",
    risToApaUseCase:
      "De bibliotheekdatabase-kortweg: exporteer je zoekresultaten als RIS, converteer ze hier, en sla het instellen van Zotero over voor een eenmalig werkstuk.",
    risToMlaTitle: "RIS-naar-MLA-converter",
    risToMlaMetaDescription:
      "Zet RIS-exports uit databases of literatuurbeheerders om in MLA-9-vermeldingen voor de literatuurlijst. Plak of upload — gratis, geen registratie.",
    risToMlaIntro:
      "JSTOR, ProQuest en EBSCO exporteren allemaal RIS, en letterenfaculteiten willen allemaal MLA. Deze pagina overbrugt die kloof: records worden lokaal verwerkt en weergegeven via de MLA-9-kernelementensjabloon, klaar om in je literatuurlijst te plakken.",
    risToMlaUseCase:
      "Gemaakt voor de avond voor de deadline: tien bronnen geëxporteerd uit JSTOR als citations.ris, en geen enkele zin om ze met de hand op te maken.",
    risToBibtexTitle: "RIS-naar-BibTeX-converter",
    risToBibtexMetaDescription:
      "Zet RIS om naar BibTeX voor LaTeX en Overleaf. Plak of upload de .ris-export, download een schoon .bib-bestand — gratis, in de browser.",
    risToBibtexIntro:
      "Op weg naar LaTeX: literatuurbeheerders en databases spreken RIS, maar de build van je paper heeft .bib nodig. Records worden volledig in je browser omgezet — typen afgeleid uit TY-tags, citeersleutels gegenereerd — tot een bestand dat Overleaf ongewijzigd accepteert.",
    risToBibtexUseCase:
      "Veelvoorkomend bij samenwerkingen: de Zotero-gebruiker exporteert RIS, de LaTeX-gebruiker laat het via deze pagina lopen, en beiden houden hun eigen gereedschap.",
    risToCslJsonTitle: "RIS-naar-CSL-JSON-converter",
    risToCslJsonMetaDescription:
      "Zet RIS-exports om naar CSL-JSON voor Pandoc- en Quarto-pipelines. Plak of upload het .ris-bestand, download references.json — gratis.",
    risToCslJsonIntro:
      "Voor gebruikers van Pandoc en Quarto is CSL-JSON het native bibliografieformaat. Voer op deze pagina de RIS in die je database of beheerder heeft geëxporteerd, en krijg een gestructureerde JSON-array terug — elk record lokaal verwerkt, niets wordt ergens geüpload.",
    risToCslJsonUseCase:
      "Past in docs-as-code-workflows: reviewers sturen RIS-bijlagen, het citatiebestand van je repo is references.json, en deze pagina is de tussenschakel.",
    cslJsonToBibtexTitle: "CSL-JSON-naar-BibTeX-converter",
    cslJsonToBibtexMetaDescription:
      "Zet CSL-JSON (exports uit Pandoc/Quarto/Zotero) om naar BibTeX. Plak of upload het .json-bestand, download het .bib-bestand — gratis, in de browser.",
    cslJsonToBibtexIntro:
      "Soms keert de stroom om: een literatuurlijst die is ontstaan in een Pandoc- of Zotero-pipeline moet een LaTeX-build voeden. Deze converter leest de CSL-JSON-array in je browser en genereert BibTeX-vermeldingen waarvan typen en velden zijn teruggekoppeld vanuit het CSL-model.",
    cslJsonToBibtexUseCase:
      "Voor de Quarto-auteur wiens co-auteur op Overleaf staat: houd references.json canoniek en genereer het .bib-bestand naar behoefte.",
    cslJsonToRisTitle: "CSL-JSON-naar-RIS-converter",
    cslJsonToRisMetaDescription:
      "Zet CSL-JSON om naar RIS, zodat Zotero, Mendeley of EndNote het kunnen importeren. Plak of upload het .json-bestand, download het .ris-bestand — gratis.",
    cslJsonToRisIntro:
      "CSL-JSON werkt uitstekend binnen Pandoc-pipelines, maar is ondoorzichtig voor de importvensters van de meeste literatuurbeheerders. Omzetten naar RIS maakt dezelfde records universeel importeerbaar — elk item wordt in je browser toegewezen aan TY/AU/TI-tags, niets wordt naar een server gestuurd.",
    cslJsonToRisUseCase:
      "Het overdrachtsprobleem: je literatuurlijst is een references.json in een git-repo, en je begeleider wil hem voor vrijdag in EndNote hebben.",
  },

  // The infinite-scroll style list shared by the country and subject pages and by the
  // /citation-styles hub. It is a client component, so these arrive as a prop rather
  // than through `getDictionary` — pulling all 17 dictionaries into the browser bundle
  // to label a search box is the thing `link-path.ts` exists to avoid.
  stylesBrowser: {
    defaultTitle: "Alle citatiestijlen",
    styleSingular: "stijl",
    stylePlural: "stijlen",
    found: "gevonden",
    filterPlaceholder: "Stijlen filteren…",
    noMatches: "Geen stijlen komen overeen met je zoekopdracht.",
    loadingMore: "Meer stijlen laden…",
    loadMore: "Meer laden ({count} resterend)",
    showingAll: "Alle {count} stijlen worden weergegeven",
    internationalLabel: "Internationaal",
  },

  footer: {
    tagline:
      "Een gratis bronvermeldingsgenerator met {count} stijlen. Geen account, geen betaald abonnement, geen limieten.",
    freeTools: "Gratis hulpmiddelen",
    resources: "Bronnen",
    legal: "Juridisch",
    language: "Taal",
    languageLabel: "Kies je taal",
    rights:
      "Bronvermeldingen worden opgemaakt met de officiële definities van de Citation Style Language.",
    popularStyles: "Populaire stijlen",
    about: "Over ons",
    privacy: "Privacy",
    terms: "Voorwaarden",
    contact: "Contact",
    copyright: "© {year} CitationEasy. Gratis te gebruiken.",
    poweredBy: "Mogelijk gemaakt door",
    allTools: "Alle schrijfhulpmiddelen",
    citationGuides: "Gidsen voor bronvermelding",
    allStyles: "Alle citatiestijlen",
    citationGlossary: "Woordenlijst bronvermelding",
    howToCiteAny: "Elke bron correct vermelden",
  },

  faq: {
    heading: "Veelgestelde vragen",
    homeQ1: "Is CitationEasy echt gratis?",
    homeA1:
      "Ja. CitationEasy is volledig gratis: geen registratie, geen limieten en geen betaalde versie. Alle {count} citatiestijlen zijn voor iedereen beschikbaar.",
    homeQ2: "Welke citatiestijlen worden ondersteund?",
    homeA2:
      "Alle {count} stijlen uit het Citation Style Language-project, waaronder APA 7e editie, MLA 9e editie, Chicago, Harvard, Vancouver, IEEE, AMA en Turabian.",
    homeQ3: "Heb ik een account nodig?",
    homeA3:
      "Nee. CitationEasy werkt volledig in je browser en je literatuurlijst blijft op je eigen apparaat staan. Er wordt niets geüpload en er valt niets te registreren.",
    homeQ4: "Kan ik mijn bronvermeldingen in mijn eigen taal maken?",
    homeA4:
      "Ja. De taal van de bronvermeldingen staat los van de taal van de site, dus je kunt in je eigen taal werken en toch verwijzingen maken in de taal waarin je schrijft.",
    styleQ1: "Is de {style}-generator gratis?",
    styleA1:
      "Ja. {style}-bronvermeldingen maken is gratis en onbeperkt, zonder account. Plak een DOI, URL, ISBN of BibTeX-item en kopieer de afgeronde verwijzing.",
    styleQ2: "Hoe verwijs ik naar een bron in {style}?",
    styleA2:
      "Plak de DOI, URL, ISBN of BibTeX-invoer van de bron in de generator op deze pagina. {style} is al geselecteerd, dus de bronvermelding en de verwijzing in de tekst verschijnen meteen.",
    styleQ3: "Hoe nauwkeurig is de {style}-opmaak?",
    styleA3:
      "Elke verwijzing wordt opgebouwd uit de officiële {style}-definitie van het Citation Style Language-project — hetzelfde bestand dat Zotero en Mendeley gebruiken — en niet uit een benadering van de regels.",
  },

  tool: {
    modeLabel: "Invoermodus voor de bronvermelding",
    modeAuto: "URL / DOI / ISBN",
    modeBook: "Boeken zoeken",
    modeArticles: "Artikelen zoeken",
    modeManual: "Handmatige invoer",
    errorEnterManually: "Gegevens handmatig invoeren",
    autoLabel: "Plak een URL, DOI, PMID, arXiv-ID, ISBN of BibTeX",
    autoHint:
      "We herkennen het type automatisch en halen de metadata op. Plak één identificatie per regel om een hele lijst in één keer te citeren.",
    generate: "Bronvermelding maken",
    generating: "Bezig…",
    bookSearchLabel: "Zoek op titel, auteur of ISBN",
    bookSearchEmpty: "Nog geen resultaten. Klik op Zoeken om boeken te vinden.",
    bookSearchSource:
      "Doorzoekt de catalogus van Open Library met miljoenen boeken",
    worksSearchLabel: "Zoek op artikeltitel of auteurs",
    worksSearchEmpty:
      "Nog geen resultaten. Klik op Zoeken om artikelen te vinden.",
    worksSearchSource:
      "Doorzoekt de catalogus van Crossref: tijdschriftartikelen, congresbijdragen en boekhoofdstukken",
    showingResults: "{shown} van {total} resultaten",
    search: "Zoeken",
    searching: "Bezig met zoeken…",
    bulkResults: "Resultaten van bulkverwerking",
    copy: "Kopiëren",
    copied: "Gekopieerd!",
    copyHint: "Kopieer en plak in je werk",
    resultTitle: "Opgemaakte bronvermelding",
    edit: "Bewerken",
    completeFields: "Aanvullen",
    fieldsIncomplete:
      "one:{count} veld onvolledig|other:{count} velden onvolledig",
    outputLanguage: "Taal",
    sourceType: "Soort bron",
    fieldAuthors: "Auteur(s)",
    fieldAuthorsHint: "Jansen, Piet of Piet Jansen",
    fieldTitle: "Titel van artikel of boek",
    fieldBookTitle: "Boektitel",
    fieldBookTitleHint: "Titel van het boek of de bundel",
    fieldEditors: "Redacteur(en)",
    fieldJournal: "Tijdschrift / publicatie",
    fieldJournalHint: "Naam van het tijdschrift of de publicatie",
    fieldPublisher: "Uitgever / site",
    fieldPublisherHint: "Naam van de uitgever of website",
    fieldDate: "Datum / jaar",
    fieldVolume: "Jaargang",
    fieldIssue: "Nummer",
    fieldPages: "Pagina('s)",
    fieldFormat: "Formaat / medium",
    fieldAuthorsGroup: "Groep of organisatie als auteur",
    fieldAuthorsGroupHint:
      "Om een groep naast personen te vermelden, zet u die tussen accolades: {R Core Team}, Piet Jansen",
    fieldTranslators: "Vertaler(s)",
    fieldInterviewers: "Interviewer(s)",
    fieldVersion: "Versie",
    fieldCourt: "Rechtbank",
    fieldIssuingAuthority: "Verlenende instantie",
    fieldPatentNumber: "Octrooinummer",
    fieldEpisodeNumber: "Afleveringsnummer",
    fieldLawNumber: "Wet- of wetsvoorstelnummer",
    fieldReportNumber: "Rapportnummer",
    fieldReviewedTitle: "Titel van het besproken werk",
    fieldReviewedAuthors: "Auteur(s) van het besproken werk",
    inTextCitation: "Verwijzing in de tekst",
    quickEdit: "Snel bewerken",
    quickEditHint: "Werk de metadata bij en maak de bronvermelding opnieuw.",
    update: "Bijwerken",
    saving: "Opslaan…",
    fieldAuthor: "Auteur",
    fieldDateShort: "Datum",
    fieldTitleShort: "Titel",
  },

  toolCollections: {
    "citation-generators": "Citatiegenerators",
    "citation-tools": "Hulpmiddelen voor citaties en referenties",
    "paper-formatting": "Opmaak van werkstukken",
    "essay-writing": "Essays en papers schrijven",
    "writing-analysis": "Tekstanalyse en redactie",
    "word-counters": "Tellers en leesbaarheid",
    vocabulary: "Woordenschat en woordzoekers",
    "research-tools": "Onderzoek en publicatie",
    statistics: "Statistiekcalculators",
    math: "Wiskundecalculators",
    science: "Natuurwetenschappelijke en laboratoriumcalculators",
    charts: "Grafieken en diagrammen",
    economics: "Economie, bedrijfskunde en financiën",
    grades: "GPA- en cijfercalculators",
    study: "Studie en productiviteit",
    classroom: "Klas en printmateriaal",
    random: "Willekeurige generators en lotingen",
    "text-tools": "Tekst opschonen en opmaken",
    "fancy-text": "Sierlijke tekst en symbolen",
    language: "Taal en transliteratie",
    ciphers: "Codes en geheimschriften",
    converters: "Omrekenen van eenheden, datums en getallen",
    developer: "Data, opmaaktalen en code",
  },
  bib: {
    title: "Mijn literatuurlijsten",
    studioTitle: "Bibliografiestudio",
    studioProjects: "Projecten",
    studioExplore: "Verkennen",
    studioExploreIntro:
      "Gidsen, tools en alle citatiestijlen zijn één klik verwijderd.",
    studioStyleGuide: "Stijlgids {style}",
    studioStyleGuideHint:
      "Regels, uitgewerkte voorbeelden en de verwijzing in de tekst",
    studioGenerator: "Bronvermeldingsgenerator",
    studioNewProject: "Nieuw project",
    studioSwitchProject: "Ander project",
    studioAddSource: "Bron toevoegen",
    studioAddSourceHint:
      "Plak een DOI, URL of ISBN, zoek boeken en artikelen of vul de gegevens handmatig in. Alles wat je opslaat komt in dit project terecht.",
    studioDoneAdding: "Klaar",
    studioReferences: "Referenties",
    studioReferenceCount: "{count} referenties",
    studioInspectorTitle: "Details van de referentie",
    studioInspectorEmpty:
      "Selecteer een referentie om de verwijzing in de tekst, notities en brongegevens te zien.",
    studioSavedLocally: "Opgeslagen in deze browser",
    studioOpen: "Bibliografiestudio openen",
    studioViewInStudio: "Bekijken in de Studio",
    studioFooterSaved: "{count} opgeslagen in je Bibliografiestudio",
    studioFooterEmpty:
      "Opgeslagen bronvermeldingen vormen in de Studio een volledige bibliografie",
    studioClearSelection: "Selectie wissen",
    studioDeleteSelected: "Selectie verwijderen",
    studioDeleteSelectedBody:
      "{count} bronvermeldingen uit ‘{name}’ verwijderen? Dit kan niet ongedaan worden gemaakt.",
    studioDeletedMany: "{count} bronvermeldingen verwijderd",
    studioMoveTo: "Verplaatsen naar",
    studioCopyTo: "Kopiëren naar",
    studioSearchShortcut: "Druk op / om te zoeken",
    studioStyle: "Stijl",
    studioCopyEntry: "Vermelding kopiëren",
    studioEntryCopied: "Vermelding gekopieerd",
    studioSourceDetails: "Brongegevens",
    studioEmptyTitle: "Dit project is leeg",
    studioEmptyBody:
      "Voeg hierboven je eerste bron toe of importeer een bestaande BibTeX-, RIS- of CSL-JSON-bibliotheek.",
    studioBackup: "Back-up",
    sharedTitle: "Gedeelde literatuurlijst",
    save: "Opslaan",
    saving: "Opslaan…",
    saved: "Opgeslagen",
    searchPlaceholder: "Bronvermeldingen zoeken…",
    empty: "Nog geen bronvermeldingen opgeslagen",
    emptySearch: "Geen bronvermeldingen komen overeen met je zoekopdracht.",
    countIn: "Bronvermeldingen in {name}: {count}",
    savedOn: "Opgeslagen op {date}",
    emptyInBib:
      "Nog geen bronvermeldingen in deze literatuurlijst. Maak en bewaar bronvermeldingen, of importeer een BibTeX-, RIS- of CSL-JSON-bibliotheek.",
    savedToast: "Bronvermelding opgeslagen in de literatuurlijst",
    copyAll: "Alles kopiëren",
    copyAllTitle: "Alle bronvermeldingen kopiëren",
    export: "Exporteren",
    exportTitle: "Literatuurlijst exporteren",
    import: "Importeren",
    importTitle: "Bronnen importeren",
    importLabel: "Bronnen importeren (BibTeX, RIS of CSL-JSON)",
    importing: "Importeren…",
    importFailed: "Importeren mislukt. Probeer het opnieuw.",
    importSummary:
      "Geïmporteerd: {added} · Duplicaten overgeslagen: {duplicates} · Boven limiet: {overLimit} · Mislukt: {failed}",
    importUnreadable: "Kon het bestand niet lezen.",
    importUnparseable: "Kon de bronnen niet importeren.",
    importReviewTitle: "Import controleren",
    importReviewIntro:
      "Controleer {total} {format}-bronnen voordat ze worden opgemaakt en opgeslagen.",
    importReviewCount: "{included} van {total} opgenomen",
    importSelectAll: "Alles opnemen",
    importExcludeAll: "Alles uitsluiten",
    importLimitNotice:
      "{count} bronnen boven de limiet van 200 items worden niet geïmporteerd.",
    importDuplicateExisting: "Mogelijk duplicaat van opgeslagen bron ‘{title}’",
    importDuplicateBatch: "Mogelijk duplicaat van ‘{title}’ in deze import",
    importMissingField: "{field} ontbreekt",
    importUntitled: "Bron zonder titel",
    importReview: "Bronnen controleren",
    importCommit: "Opmaken en importeren",
    importBack: "Terug",
    importOutcomeTitle: "Import voltooid",
    importOutcomeSummary:
      "Toegevoegd: {added} · Duplicaten overgeslagen: {duplicates} · Uitgesloten: {excluded} · Boven limiet: {overLimit} · Mislukt: {failed}",
    sortBy: "Sorteren op",
    sortTitle: "Literatuurlijst sorteren",
    sortAuthor: "Auteur (A–Z)",
    sortNewest: "Toegevoegd op (nieuwste)",
    sortOldest: "Jaar (oudste eerst)",
    sortManual: "Handmatige volgorde",
    backupAll: "Volledige projectback-up (.json)",
    backupDownloaded: "Volledige projectback-up gedownload",
    backupUnreadable: "Het back-upbestand kon niet worden gelezen.",
    restore: "Herstellen",
    restoreTitle: "Alle bibliografieprojecten herstellen",
    restoreWarning:
      "De back-up wordt eerst gevalideerd en vervangt daarna alle bibliografieën die in deze browser zijn opgeslagen. Dit kan niet ongedaan worden gemaakt.",
    restoreChoose: "Kies een CitationEasy-back-up (.json)",
    replaceAndRestore: "Vervangen en herstellen",
    restoreComplete: "Alle bibliografieprojecten zijn hersteld",
    restoreInvalid: "Dit is geen geldige CitationEasy-projectback-up.",
    selectAllVisible: "Alles wat wordt getoond selecteren",
    selectedCount: "{count} geselecteerd",
    clusterOpen: "Samen citeren",
    clusterTitle: "Samengestelde verwijzing in de tekst",
    clusterDescription:
      "Maak één citatiecluster van de geselecteerde bronnen. De gekozen stijl bepaalt de volgorde, interpunctie en nummering.",
    clusterTarget: "Uitvoer",
    clusterLocatorOptional: "Optionele vindplaats",
    clusterBuildCopy: "Citaat maken en kopiëren",
    clusterBuilding: "Citaat wordt gemaakt…",
    clusterCopied: "Samengesteld citaat gekopieerd",
    clusterFailed:
      "Het samengestelde citaat kon niet worden gemaakt. Probeer het opnieuw.",
    destination: "Doelbibliografie",
    chooseDestination: "Kies een bibliografie…",
    copySelected: "Kopiëren",
    moveSelected: "Verplaatsen",
    copyComplete:
      "{count} citaten gekopieerd. {skipped} duplicaten zijn overgeslagen.",
    moveComplete:
      "{count} citaten verplaatst. {skipped} duplicaten bleven in de bron.",
    selectCitation: "{title} selecteren",
    moveUp: "Omhoog verplaatsen",
    moveDown: "Omlaag verplaatsen",
    deleteTitle: "Bronvermelding verwijderen",
    deleteConfirm: "Bronvermelding verwijderen?",
    deleted: "Bronvermelding verwijderd",
    editTitle: "Bronvermelding bewerken",
    close: "Sluiten",
    copiedAll: "Alle bronvermeldingen gekopieerd!",
    copiedAnnotated: "Geannoteerde literatuurlijst gekopieerd!",
    copiedBibtex: "BibTeX gekopieerd!",
    copiedBulleted: "Opsomming gekopieerd!",
    copiedNumbered: "Genummerde lijst gekopieerd!",
    copiedHtml: "HTML-fragment gekopieerd!",
    downloadedWord: "Word-document gedownload",
    downloadedRtf: "RTF-bestand gedownload",
    downloadedRis: "RIS-bestand gedownload",
    downloadedCsl: "CSL-JSON-bestand gedownload",
    downloadedAnnotated: "Geannoteerde literatuurlijst gedownload",
    copyFailed: "Kopiëren naar het klembord mislukt",
    shareCopied:
      "Link gekopieerd! Iedereen met deze link kan de lijst bekijken en importeren.",
    shareFailed: "Kon de deellink niet maken",
    regenerateFailed: "Kon de bronvermelding niet opnieuw genereren",
    updateFailed: "Kon de bronvermelding niet bijwerken",
    fieldAuthor: "Auteur",
    fieldTitle: "Titel",
    fieldDate: "Datum",
    fieldPublisher: "Uitgever",
    fieldVolume: "Jaargang",
    fieldIssue: "Nummer",
    fieldPages: "Pagina('s)",
    fieldUrl: "URL",
    fieldAccessed: "Raadpleegdatum",
    fieldEditor: "Redacteur",
    fieldEvent: "Locatie evenement",
    noteLabel: "Annotatie bij deze bron",
    noteEdit: "Deze notitie bewerken",
    notePlaceholder:
      "Vat de bron samen, beoordeel hem en leg uit hoe hij in je werk past…",
    noteRemoved: "Notitie verwijderd",
    noteSave: "Notitie opslaan",
    noteCancel: "Annuleren",
    noteAdd: "Notitie toevoegen",
    noteWords: "{count} woorden",
    noteSaved: "Notitie opgeslagen ({count} woorden)",
    noteClickToEdit: "klik om te bewerken",
    inTextCopy: "De verwijzing in de tekst voor deze bron kopiëren",
    inTextSelect: "Selecteer de verwijzing hieronder om te kopiëren.",
    inTextFailed:
      "Kon de verwijzing in de tekst niet opbouwen. Probeer het opnieuw.",
    locator: "Vindplaats",
    locatorType: "Soort vindplaats",
    locatorPage: "Pagina",
    locatorChapter: "Hoofdstuk",
    locatorSection: "Paragraaf",
    locatorParagraph: "Alinea",
    locatorLine: "Regel",
    locatorVerse: "Vers",
    reformat: "Opnieuw opmaken",
    reformatTitle: "Alle bronvermeldingen in een andere stijl opmaken",
    reformatHeading: "Alles in één stijl opmaken",
    reformatChoose: "Kies een stijl…",
    reformatFailed:
      "Opnieuw opmaken mislukt. Je literatuurlijst is ongewijzigd.",
    sortOrder: "Sorteercriterium",
    copyToClipboard: "Naar klembord kopiëren",
    numberedList: "Genummerde lijst",
    bulletedList: "Opsomming",
    htmlSnippet: "HTML-fragment",
    copyWithAnnotations: "Kopiëren met annotaties",
    wordAnnotated: "Word-document met annotaties (.doc)",
    downloadFile: "Bestand downloaden",
    wordDocument: "Word-document (.doc)",
    copyShareLink: "Deellink kopiëren",
    print: "Afdrukken…",
    printFailed: "Pop-up geblokkeerd — sta pop-ups toe om af te drukken",
    font: "Lettertype",
    sitePublication: "Website / publicatienaam",
    importIntro:
      "Plak of upload een BibTeX- (.bib), RIS- (.ris) of CSL-JSON-bestand (.json)",
    importUpload: "Upload een .bib-, .ris- of .json-bestand",
    importPaste: "Of plak BibTeX, RIS of CSL-JSON",
    cancel: "Annuleren",
    done: "Gereed",
    delete: "Verwijderen",
    deleteBody:
      "Deze bronvermelding wordt definitief uit je literatuurlijst verwijderd.",
    editBody:
      "Werk de metadata bij en sla op om de bronvermelding opnieuw te genereren.",
    reformatExplain:
      "Bouwt alle {count} vermeldingen opnieuw op uit de opgeslagen brongegevens — er wordt niets opnieuw gedownload en geen bron gaat verloren.",
    inTextHeading: "Verwijzing in de tekst",
    inTextLocatorHint:
      "Voeg een pagina of andere vindplaats toe om de verwijzing aan een plek in de bron te koppelen — bij letterlijke citaten vereist door de meeste stijlen. Laat leeg voor de eenvoudige vorm.",
    inTextCopyButton: "Verwijzing in de tekst kopiëren",
    mixedStyles: "Deze literatuurlijst mengt {count} citeerstijlen ({styles}).",
    mixedStylesAction:
      "Gebruik {action} om alles in één stijl te zetten voordat je hem inlevert.",
    selectBibliography: "Literatuurlijst selecteren",
    newBibliography: "Nieuwe literatuurlijst",
    moreOptions: "Meer opties",
    rename: "Naam wijzigen",
    removeDuplicates: "Duplicaten verwijderen",
    cannotDeleteOnly: "Je kunt je enige literatuurlijst niet verwijderen",
    newBibliographyBody:
      "Maak een literatuurlijst voor een ander werkstuk of project.",
    bibliographyName: "Naam van literatuurlijst",
    create: "Maken",
    renameBibliography: "Literatuurlijst hernoemen",
    renameBibliographyBody: "Voer een nieuwe naam in voor ‘{name}’.",
    newName: "Nieuwe naam",
    duplicateRemovedOne: "{count} duplicaat verwijderd",
    duplicateRemovedMany: "{count} duplicaten verwijderd",
    noDuplicates: "Geen dubbele referenties gevonden",
    removeDuplicatesConfirm: "Dubbele referenties verwijderen?",
    removeDuplicatesBody:
      "Hiermee wordt ‘{name}’ gecontroleerd op dezelfde bron (DOI, ISBN of titel, jaar en auteur) en blijft één exemplaar staan. Niet-vergelijkbare referenties blijven ongewijzigd.",
    deleteBibliographyConfirm: "Literatuurlijst verwijderen?",
    deleteBibliographyBody:
      "Hiermee worden ‘{name}’ en alle {count} citaten erin permanent verwijderd.",
  },

  bibliographyPage: {
    metaTitle: "Bibliografiestudio - citaten ordenen, annoteren en exporteren",
    metaDescription:
      "Maak en beheer privé-bibliografieprojecten in je browser. Zoek, sorteer, annoteer, bewerk, formatteer, importeer, exporteer en deel citaten zonder account.",
    keyword1: "bibliografie maken",
    keyword2: "citaten beheren",
    keyword3: "geannoteerde bibliografie",
    ogTitle: "Bibliografiestudio",
    ogDescription:
      "Een privéwerkruimte voor het ordenen, opmaken, annoteren, importeren en exporteren van citaten.",
    ogImageTitle: "Bibliografiestudio",
    ogImageSubtitle:
      "Orden, annoteer, formatteer, exporteer en deel je referenties.",
    ogKind: "Citatenwerkruimte",
    breadcrumb: "Bibliografiestudio",
    eyebrow: "Privéwerkruimte in de browser",
    lead: "Beheer alle referenties voor een werkstuk op één plek. Je projecten blijven in deze browser, tenzij je ze bewust exporteert of deelt.",
    privacyTitle: "Standaard privé",
    privacyBody:
      "Projecten en notities gebruiken lokale browseropslag. Deellinks bevatten citaten, maar laten privénotities bewust weg.",
    workflowTitle: "Van bronnenlijst naar eindversie",
    workflowBody:
      "Wissel van project, zoek en sorteer referenties, voeg notities of vindplaatsen toe, corrigeer metadata en formatteer de hele lijst in één stijl.",
    formatsTitle: "Importeer referenties en neem ze overal mee",
    formatsBody:
      "Importeer BibTeX, RIS of CSL-JSON. Kopieer opgemaakte lijsten of exporteer Word, RTF, RIS, CSL-JSON, HTML, BibTeX en geannoteerde versies.",
  },

  // The 95-term glossary: /glossary and /glossary/[slug].
  //
  // The chrome only. Each term's own prose — `short`, the `body` paragraphs, the
  // example — is ~10,600 words that cannot live in a flat string map and must not be
  // copied verbatim into sixteen files; it lives in `src/content/glossary/<locale>.json`
  // and is read through `lib/localized-content.ts`.
  glossaryPage: {
    metaTitle: "{term} - definitie en voorbeelden",
    notFoundTitle: "Term niet gevonden",
    keywordDefinition: "definitie van {term}",
    keywordWhatIs: "wat is {term}",
    ogTitle: "{term} - Verklarende woordenlijst van CitationEasy",
    ogKind: "Citatiewoordenlijst",
    breadcrumb: "Woordenlijst",
    alsoCalled: "Ook wel: {aliases}",
    doItHeading: "Doe het op CitationEasy",
    seeAlsoHeading: "Zie ook",
    moreInHeading: "Meer in {category}",
    navLabel: "Navigatie woordenlijst",
    allTermsLink: "Alle {count} termen in de woordenlijst",
    generatorLink: "Gratis bronvermeldingengenerator",
    termSetName: "Citatiewoordenlijst van CitationEasy",
    hubMetaTitle: "Citatiewoordenlijst - {count} termen uitgelegd",
    hubMetaDescription:
      "Duidelijke definities van {count} termen over citeren en academisch schrijven: et al., ibid., DOI, hangende inspringing, patchwriting, definitieve versie en meer — elk met voorbeelden.",
    hubKeyword1: "citatiewoordenlijst",
    hubKeyword2: "citatietermen",
    hubKeyword3: "woordenlijst academisch schrijven",
    hubKeyword4: "wat betekent et al",
    hubKeyword5: "wat is een DOI",
    hubKeyword6: "terminologie bronvermelding",
    hubOgTitle: "Citatiewoordenlijst",
    hubOgDescription:
      "Definities van {count} termen over citeren en academisch schrijven, met voorbeelden.",
    hubOgImageTitle: "Citatiewoordenlijst",
    hubOgImageSubtitle:
      "{count} termen over citeren en academisch schrijven, in gewone taal uitgelegd met voorbeelden.",
    hubOgKind: "Naslagwerk",
    hubTermSetDescription:
      "Definities van {count} termen over citeren, bronvermelding en academisch schrijven.",
    hubCollectionName: "Citatiewoordenlijst",
    hubCollectionDescription:
      "Duidelijke definities van {count} termen over citeren en academisch schrijven.",
    hubHeading: "Citatiewoordenlijst",
    hubIntro:
      "{count} termen uit citeren en academisch schrijven, in gewone taal uitgelegd met voorbeelden — de afkortingen die in stijlgidsen voorkomen, de onderdelen waaruit een bronvermelding is opgebouwd, de identificatoren die een bron precies vastleggen, en het vocabulaire van wetenschappelijke publicaties.",
    hubJumpLabel: "Ga naar letter",
    hubBrowseHeading: "Bekijk per onderwerp",
    hubAllHeading: "Alle termen A–Z",
    hubWorkHeading: "Pas de termen toe",
    hubWorkGenerator: "Gratis bronvermeldingengenerator",
    hubWorkStyles: "Handleidingen voor citatiestijlen",
    hubWorkHowTo: "Hoe citeer je elke bron",
    hubWorkCompare: "Vergelijk citatiestijlen",
    hubWorkTools: "Schrijf- en citatiehulpmiddelen",
    hubWorkFaq: "Veelgestelde vragen over citeren",
  },

  // /faq — the 17-question hub. The questions and answers themselves are in
  // `src/data/faq.ts` (the one source the API, the MCP tool and /md also read); this is
  // the page around them.
  faqPage: {
    metaTitle:
      "Citeer-FAQ - Snelle antwoorden op veelgestelde vragen over citeren",
    metaDescription:
      "Snelle, nauwkeurige antwoorden op de vragen over citeren die studenten echt stellen: DOI's, bronnen zonder auteur, et al., hangende inspringing, AI citeren, regels over plagiaat, APA versus MLA en meer — met links naar volledige handleidingen.",
    ogTitle: "Citeer-FAQ",
    ogDescription:
      "Snelle antwoorden op de meest gestelde vragen over citeren, van DOI's en et al. tot het citeren van ChatGPT.",
    ogImageTitle: "Vragen en antwoorden over citeren",
    ogImageSubtitle:
      "Korte, nauwkeurige antwoorden op de vragen die studenten echt stellen over citeren.",
    ogKind: "FAQ",
    breadcrumbLabel: "Kruimelpad",
    breadcrumbHome: "Home",
    breadcrumbCurrent: "Citeer-FAQ",
    heading: "Citeer-FAQ",
    intro:
      "Snelle, nauwkeurige antwoorden op de vragen over citeren die studenten echt stellen — met links naar de volledige handleiding of tool wanneer je meer diepgang nodig hebt.",
    contentsLabel: "Inhoud van de FAQ",
    stillStuckHeading: "Nog steeds vastzitten?",
    stillStuckBody:
      "Blader door de [volledige handleidingenbibliotheek](/guides), bekijk de [stijl-spiekbriefjes](/cheat-sheets), zoek een term op in de [citeerwoordenlijst](/glossary), [doorzoek de hele site](/search), of [genereer meteen de bronvermelding](/) en laat de opmaaktool de regels voor je toepassen.",
  },

  // /cheat-sheets and its three style sheets. The citation FORMATS in them
  // ("Author, A. A. (Year)…") are the styles' own templates and stay as they are in
  // every language, exactly as the per-style examples do; the labels around them do not.
  cheatSheets: {
    hubMetaTitle: "Spiekbriefjes voor citatiestijlen - Snelle naslaggidsen",
    hubMetaDescription:
      "Spiekbriefjes voor snelle naslag van de citatiestijlen APA, MLA en Chicago. Printvriendelijke gidsen met voorbeelden van veelvoorkomende brontypen.",
    hubBreadcrumbHome: "Home",
    hubBreadcrumbCurrent: "Spiekbriefjes voor citatiestijlen",
    hubBackLink: "← Terug naar generator",
    hubHeading: "Spiekbriefjes voor citatiestijlen",
    hubIntro:
      "Snelle naslaggidsen voor de populairste citatiestijlen. Printvriendelijk en altijd actueel.",
    hubApaTitle: "Spiekbriefje APA 7e editie",
    hubApaDesc: "Psychologie, sociale wetenschappen, pedagogiek, verpleegkunde",
    hubMlaTitle: "Spiekbriefje MLA 9e editie",
    hubMlaDesc:
      "Letterkunde, talen, geesteswetenschappen, cultuurwetenschappen",
    hubChicagoTitle: "Spiekbriefje Chicago-stijl",
    hubChicagoDesc: "Geschiedenis, kunstgeschiedenis, filosofie, religie",
    backToSheets: "← Terug naar spiekbriefjes",
    generalRules: "Algemene regels",
    inTextCitations: "Verwijzingen in de tekst",
    colSituation: "Situatie",
    colFormat: "Formaat",
    colExample: "Voorbeeld",
    book: "Boek",
    journalArticle: "Tijdschriftartikel",
    website: "Website",
    apaMetaTitle: "Spiekbriefje APA 7e editie - Snelle naslaggids",
    apaMetaDescription:
      "APA 7e editie citatiespiekbriefje met voorbeelden voor boeken, tijdschriften, websites en meer. Printvriendelijke snelle naslag.",
    apaHeading: "Spiekbriefje APA 7e editie",
    apaLead: "Snelle naslag voor de meest voorkomende APA-citatieformaten.",
    apaRule1:
      'Gebruik een **hangende inspringing** (eerste regel links uitgelijnd, volgende regels 0,5" ingesprongen)',
    apaRule2: "Gebruik dubbele regelafstand voor alle vermeldingen",
    apaRule3: "Alfabetiseer op achternaam van de auteur",
    apaRule4: "Zet titels van boeken, tijdschriften en delen cursief",
    apaRule5: "Gebruik de DOI indien beschikbaar",
    apaRow1Situation: "1 auteur",
    apaRow1Format: "(Author, Year)",
    apaRow1Example: "(Smith, 2024)",
    apaRow2Situation: "2 auteurs",
    apaRow2Format: "(Author & Author, Year)",
    apaRow2Example: "(Smith & Jones, 2024)",
    apaRow3Situation: "3+ auteurs",
    apaRow3Format: "(First Author et al., Year)",
    apaRow3Example: "(Smith et al., 2024)",
    apaRow4Situation: "Direct citaat",
    apaRow4Format: "(Author, Year, p. #)",
    apaRow4Example: "(Smith, 2024, p. 45)",
    apaRow5Situation: "Geen auteur",
    apaRow5Format: '("Title," Year)',
    apaRow5Example: '("Study Results," 2024)',
    apaReferenceHeading: "Voorbeelden van de literatuurlijst",
    apaBookExample:
      "Author, A. A. (Year). *Title of work: Capital letter also for subtitle*. Publisher. https://doi.org/xxxxx",
    apaJournalExample:
      "Author, A. A., & Author, B. B. (Year). Title of article. *Title of Periodical, Volume*(Issue), Page–Page. https://doi.org/xxxxx",
    apaWebsiteExample:
      "Author, A. A. (Year, Month Day). *Title of page*. Site Name. https://www.example.com/page",
    apaChapterLabel: "Hoofdstuk uit een geredigeerd boek",
    apaChapterExample:
      "Author, A. A. (Year). Title of chapter. In E. E. Editor (Ed.), *Title of book* (pp. #–#). Publisher.",
    apaConferenceLabel: "Congresbijdrage",
    apaConferenceExample:
      "Author, A. A. (Year, Month Days). *Title of paper* [Paper presentation]. Conference Name, Location.",
    apaDifferencesHeading: "Belangrijkste verschillen met APA 6",
    apaDifference1: "Geen «Retrieved from» meer vóór URL's",
    apaDifference2: "Vermeld de DOI in het linkformaat https://doi.org/",
    apaDifference3: "Tot 20 auteurs vermeld voordat puntjes worden gebruikt",
    apaDifference4: "Vestigingsplaats van de uitgever niet meer vereist",
    apaDifference5:
      "Gebruik het Engelse enkelvoudige «they» voor genderneutraal taalgebruik",
    apaCtaText: "Genereer automatisch perfecte APA-bronvermeldingen",
    apaCtaButton: "APA-bronvermeldingengenerator →",
    mlaMetaTitle: "Spiekbriefje MLA 9e editie - Snelle naslaggids",
    mlaMetaDescription:
      "MLA 9e editie citatiespiekbriefje met voorbeelden voor boeken, artikelen, websites en meer. Printvriendelijke snelle naslag.",
    mlaHeading: "Spiekbriefje MLA 9e editie",
    mlaLead: "Snelle naslag voor de meest voorkomende MLA-citatieformaten.",
    mlaRule1: "Gebruik een **hangende inspringing**",
    mlaRule2: "Gebruik dubbele regelafstand voor alle vermeldingen",
    mlaRule3: "Alfabetiseer op achternaam van de auteur",
    mlaRule4: "Geef de pagina de titel «Geciteerde werken»",
    mlaRule5:
      "Zet titels van containers (boeken, tijdschriften, websites) cursief",
    mlaRow1Situation: "1 auteur",
    mlaRow1Format: "(Author Page)",
    mlaRow1Example: "(Smith 45)",
    mlaRow2Situation: "2 auteurs",
    mlaRow2Format: "(Author and Author Page)",
    mlaRow2Example: "(Smith and Jones 45)",
    mlaRow3Situation: "3+ auteurs",
    mlaRow3Format: "(First Author et al. Page)",
    mlaRow3Example: "(Smith et al. 45)",
    mlaRow4Situation: "Geen auteur",
    mlaRow4Format: '("Title" Page)',
    mlaRow4Example: '("Study Results" 45)',
    mlaRow5Situation: "Geen paginanummer",
    mlaRow5Format: "(Author)",
    mlaRow5Example: "(Smith)",
    mlaWorksCitedHeading: "Voorbeelden van geciteerde werken",
    mlaBookExample: "Last, First. *Title of Book*. Publisher, Year.",
    mlaJournalExample:
      'Last, First. "Title of Article." *Journal Title*, vol. #, no. #, Year, pp. #–#.',
    mlaWebsiteExample:
      'Last, First. "Title of Page." *Website Name*, Day Month Year, URL.',
    mlaAnthologyLabel: "Bloemlezing/geredigeerd boek",
    mlaAnthologyExample:
      'Last, First. "Title of Essay." *Title of Collection*, edited by First Last, Publisher, Year, pp. #–#.',
    mlaCoreHeading: "MLA-kernelementen (in volgorde)",
    mlaCore1: "Author.",
    mlaCore2: '"Title of Source."',
    mlaCore3: "*Title of Container*,",
    mlaCore4: "Contributors,",
    mlaCore5: "Version,",
    mlaCore6: "Number,",
    mlaCore7: "Publisher,",
    mlaCore8: "Publication date,",
    mlaCore9: "Location (pages, URL, DOI).",
    mlaCtaText: "Genereer automatisch perfecte MLA-bronvermeldingen",
    mlaCtaButton: "MLA-bronvermeldingengenerator →",
    chicagoMetaTitle: "Spiekbriefje Chicago-stijl - Snelle naslaggids",
    chicagoMetaDescription:
      "Chicago Manual of Style citatiespiekbriefje met voorbeelden voor Noten-bibliografie en Auteur-jaar. Printvriendelijke snelle naslag.",
    chicagoHeading: "Spiekbriefje Chicago-stijl",
    chicagoLead:
      "Snelle naslag voor citatieformaten van de Chicago Manual of Style (18e editie).",
    chicagoSystemsHeading: "Twee Chicago-systemen",
    chicagoColNb: "Noten-bibliografie (NB)",
    chicagoColAd: "Auteur-jaar (AJ)",
    chicagoRow1Nb: "Voetnoten/eindnoten + bibliografie",
    chicagoRow1Ad: "Verwijzingen tussen haakjes + literatuurlijst",
    chicagoRow2Nb: "Geschiedenis, kunst, geesteswetenschappen",
    chicagoRow2Ad: "Wetenschappen, sociale wetenschappen",
    chicagoRow3Nb: "Uitgebreidere eerste noten",
    chicagoRow3Ad: "Vergelijkbaar met APA-formaat",
    chicagoExamplesHeading: "Voorbeelden van noten-bibliografie",
    chicagoNoteLabel: "Noot:",
    chicagoBibliographyLabel: "Bibliografie:",
    chicagoBookLabel: "Boek (voetnoot)",
    chicagoBookNote: "1. First Last, *Title of Book* (Publisher, Year), page.",
    chicagoBookBibliography: "Last, First. *Title of Book*. Publisher, Year.",
    chicagoJournalLabel: "Tijdschriftartikel (voetnoot)",
    chicagoJournalNote:
      '1. First Last, "Title of Article," *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoJournalBibliography:
      'Last, First. "Title of Article." *Journal Title* Vol, no. Issue (Year): pages.',
    chicagoWebsiteLabel: "Website (voetnoot)",
    chicagoWebsiteNote:
      '1. First Last, "Title of Page," Site Name, Month Day, Year, URL.',
    chicagoWebsiteBibliography:
      'Last, First. "Title of Page." Site Name. Month Day, Year. URL.',
    chicagoShortHeading: "Verkorte noten",
    chicagoShortIntro:
      "Gebruik na de eerste volledige bronvermelding verkorte voetnoten:",
    chicagoShortExample: "1. Last, *Short Title*, page.",
    chicagoRulesHeading: "Belangrijkste opmaakregels",
    chicagoRule1:
      "Gebruik superscriptcijfers voor voetnootverwijzingen in de tekst",
    chicagoRule2: "Bibliografievermeldingen gebruiken een hangende inspringing",
    chicagoRule3:
      "Gebruik voor opeenvolgende verwijzingen naar dezelfde bron een verkorte noot in plaats van «Ibid.»",
    chicagoRule4:
      "Titels: boeken en tijdschriften cursief, artikelen en hoofdstukken tussen aanhalingstekens",
    chicagoRule5:
      "Gebruik raadpleegdata voor onlinebronnen die kunnen veranderen",
    chicagoCtaText: "Genereer automatisch perfecte Chicago-bronvermeldingen",
    chicagoCtaButton: "Chicago-bronvermeldingengenerator →",
  },

  // The catalog's page-type labels (`CATALOG_TYPE_LABELS`), which the search page shows
  // as facet chips and beside every result. Layer 2: a fact from `src/lib/**` rather
  // than page chrome, and English until it is mapped through here.
  catalogTypes: {
    core: "Kernpagina's",
    styleLanding: "Hubs voor de belangrijkste citatiestijlen",
    style: "Referentiepagina's voor citatiestijlen",
    styleExamples: "Voorbeeldpagina's voor citaties",
    styleFormat: "Handleidingen voor de opmaak van je werkstuk",
    styleInText: "Handleidingen voor verwijzingen in de tekst",
    styleAnnotatedBibliography:
      "Handleidingen voor geannoteerde bibliografieën",
    styleReferencePage:
      "Handleidingen voor de literatuurlijst/geciteerde werken",
    tool: "Gratis schrijf- en citatiehulpmiddelen",
    guide: "Educatieve handleidingen",
    guideCategory: "Gidscategorieën",
    toolCollection: "Collecties van hulpmiddelen",
    howToCite: "Hoe citeer je <bron> in <stijl>",
    comparison: "Vergelijkingen van citatiestijlen",
    converter: "Identificatie- en formaatconverters",
    field: "Citatiestijlen per vakgebied",
    region: "Citatiestijlen per land/regio",
    category: "Citatiestijlen per categorie",
    cheatSheet: "Eenpagina-spiekbriefjes",
    glossary: "Verklarende woordenlijst voor citeren en academisch schrijven",
  },

  // /about
  aboutPage: {
    metaTitle: "Over CitationEasy - Gratis generator voor bronvermeldingen",
    metaDescription:
      "CitationEasy is een gratis generator voor bronvermeldingen die {count} stijlen ondersteunt. Gebouwd door Samuel Sergi met open source-tools zoals Citation Style Language, Citation.js en Next.js.",
    backLink: "← Terug naar generator",
    heading: "Over CitationEasy",
    intro:
      "CitationEasy is een gratis, vrij toegankelijke generator voor bronvermeldingen, gebouwd voor studenten, onderzoekers en academische schrijvers. Hij ondersteunt **{count} citatiestijlen** en biedt een groeiende verzameling schrijfhulpmiddelen, educatieve handleidingen en naslagbronnen — allemaal zonder dat je je hoeft aan te melden of te abonneren.",
    offerHeading: "Wat we bieden",
    offer1:
      "[Generator voor bronvermeldingen](/) met {count} stijlen, waaronder APA 7, MLA 9, Chicago 18, Harvard, Vancouver, IEEE en duizenden andere",
    offer2:
      "[40 gratis schrijf- en citatiehulpmiddelen](/tools) — woordenteller, leesbaarheidscontrole, DOI-validator, alfabetiseerder voor literatuurlijsten en meer",
    offer3:
      "[750 handleidingen over hoe te citeren](/how-to-cite) die elk belangrijk brontype in 6 citatiestijlen behandelen",
    offer4:
      "[Meer dan 100 educatieve handleidingen](/guides) over beste praktijken voor citeren, academisch schrijven en onderzoeksvaardigheden",
    offer5:
      "[Tool voor het vergelijken van stijlen](/compare), [quiz voor citatiestijlen](/quiz) en [spiekbriefjes](/cheat-sheets) voor snelle naslag",
    offer6:
      "7 exportformaten: platte tekst, BibTeX, Word, genummerde lijst, opsommingslijst, RIS en CSV",
    openSourceHeading: "Gebouwd met open source",
    openSourceIntro:
      "CitationEasy is mogelijk gemaakt door een aantal uitstekende open source-projecten en API's. Wij geloven in het geven van erkenning waar die toekomt:",
    openSource1:
      "[Citation Style Language (CSL)](https://citationstyles.org/) — de open standaard waarop onze {count} citatiestijlen draaien. CSL wordt onderhouden door een gemeenschap van vrijwilligers en wordt gebruikt door referentiebeheerders zoals Zotero, Mendeley en Papers. De stijldefinities komen uit de [CSL Styles Repository](https://github.com/citation-style-language/styles) op GitHub.",
    openSource2:
      "[Citation.js](https://citation.js.org/) — de JavaScript-bibliotheek die bronvermeldingen verwerkt en opmaakt volgens CSL-stijlen. Gemaakt door Lars Willighagen.",
    openSource3:
      "[Open Library API](https://openlibrary.org/developers/api) — de gratis boekenzoek-API van Internet Archive, gebruikt om boekmetadata op te zoeken via titel of ISBN.",
    openSource4:
      "[DOI Foundation](https://www.doi.org/) — het Digital Object Identifier-systeem dat wordt gebruikt om DOI's te herleiden en te valideren.",
    createdHeading: "Gemaakt door",
    createdBody:
      "CitationEasy is gemaakt door en wordt onderhouden door **Samuel Sergi**. Het doel is simpel: een echt gratis, hoogwaardige citatietool bieden die functies niet achter een betaalmuur verstopt en geen account vereist.",
    contactPrompt: "Heb je vragen, feedback of suggesties? Neem contact op:",
    contactEmail: "E-mail: [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    missionHeading: "Onze missie",
    mission1:
      "Nauwkeurige bronvermeldingen zouden geen geld moeten kosten. Veel citatietools adverteren als gratis, maar beperken exportformaten, beperken het aantal bronvermeldingen of duwen gebruikers richting betaalde abonnementen. CitationEasy zet zich in om volledig gratis te blijven — geen aanmelding, geen limieten, geen premiumniveau.",
    mission2:
      "Elke functie op deze site, van de generator voor bronvermeldingen tot de schrijfhulpmiddelen en educatieve handleidingen, is voor iedereen gratis beschikbaar. Wij geloven dat hulpmiddelen voor academische integriteit toegankelijk moeten zijn voor alle studenten en onderzoekers, ongeacht hun budget.",
  },

  // /contact
  contactPage: {
    metaTitle: "Contact",
    metaDescription:
      "Neem contact op met CitationEasy. Vragen, feedback of suggesties over onze gratis generator voor bronvermeldingen en schrijfhulpmiddelen? We horen graag van je.",
    backLink: "← Terug naar generator",
    heading: "Contact",
    intro:
      "Heb je vragen, feedback of suggesties over CitationEasy? We horen graag van je. Of je nu een bug hebt gevonden, een functiewens hebt of hulp nodig hebt met een bronvermelding: aarzel niet om contact op te nemen.",
    touchHeading: "Neem contact op",
    touchBody:
      "De beste manier om ons te bereiken is per e-mail. We proberen binnen 48 uur te reageren.",
    emailLine: "**E-mail:** [sam@sammysergi.com](mailto:sam@sammysergi.com)",
    commonHeading: "Veelgestelde vragen",
    commonIntro:
      "Voordat je contact opneemt, vind je het antwoord op je vraag misschien al in een van deze bronnen:",
    resource1:
      "[Citeerhandleidingen](/guides) — stapsgewijze instructies voor het citeren van bronnen in elke belangrijke stijl",
    resource2:
      "[Handleidingen over hoe te citeren](/how-to-cite) — 750 combinaties van brontype en stijl, met voorbeelden",
    resource3:
      "[Spiekbriefjes](/cheat-sheets) — snelle naslag voor APA-, MLA- en Chicago-opmaak",
    resource4:
      "[Schrijfhulpmiddelen](/tools) — woordenteller, leesbaarheidscontrole, DOI-validator en meer",
    issueHeading: "Meld een probleem",
    issueIntro:
      "Als je een opmaakfout in een bronvermelding, een kapotte functie of een onjuiste stijl hebt gevonden, geef dan zo veel mogelijk details wanneer je contact met ons opneemt:",
    issue1: "De citatiestijl die je gebruikte",
    issue2: "Het brontype (boek, website, tijdschriftartikel, enz.)",
    issue3: "Wat je verwachtte versus wat er werd gegenereerd",
    issue4: "Je browser en apparaat (indien relevant)",
    issueOutro: "Dit helpt ons het probleem snel te herkennen en op te lossen.",
    aboutHeading: "Over CitationEasy",
    aboutBody:
      "CitationEasy is een gratis generator voor bronvermeldingen die meer dan 10.000 citatiestijlen ondersteunt. Het is gemaakt door Samuel Sergi met als doel nauwkeurige bronvermeldingen voor iedereen toegankelijk te maken. Lees meer op onze [Over-pagina](/about).",
  },

  // /ai — the agent and developer docs.
  //
  // The code samples, endpoint paths and MCP tool NAMES are deliberately absent: they are
  // the API's identifiers, served in English by the API itself, and a translated one
  // would document an endpoint that does not exist.
  aiPage: {
    metaTitle:
      "CitationEasy voor AI-assistenten en -agents - API, MCP-server, llms.txt",
    metaDescription:
      "Gebruik CitationEasy programmatisch: een gratis, niet-geauthenticeerde JSON API voor bronvermeldingen in {count} stijlen, een MCP-server voor agents, llms.txt, een OpenAPI-specificatie en markdown-weergaven van elke pagina.",
    keyword1: "citatie-API",
    keyword2: "MCP-server",
    keyword3: "API voor bronvermeldingengenerator",
    keyword4: "llms.txt",
    keyword5: "OpenAPI",
    keyword6: "bronvermeldingen voor AI-agents",
    keyword7: "gratis citatie-API",
    webApiDescription:
      "Gratis, niet-geauthenticeerde JSON API die bronvermeldingen opmaakt in {count} Citation Style Language-stijlen op basis van een URL, DOI, ISBN, PubMed-ID, arXiv-ID of CSL-JSON-metadata.",
    breadcrumb: "Voor AI en agents",
    heading: "CitationEasy voor AI-assistenten en agents",
    intro:
      "Alles op deze site is beschikbaar voor software én voor mensen: een gratis JSON API, een MCP-server, een OpenAPI-specificatie, een llms.txt-index en een markdown-weergave van alle {count} pagina's. Geen sleutel, geen account, geen eigen ratelimiet.",
    whyHeading:
      "Waarom de API aanroepen in plaats van zelf de bronvermelding te schrijven",
    whyBody:
      "Bronvermeldingen opmaken lijkt een tekstgeneratieprobleem, maar dat is het niet. Elke stijl legt honderden regels vast over het omkeren van auteursnamen, initialen, hoofdlettergebruik in titels, interpunctie van containers, editievermeldingen en de plaatsing van de datum, en die regels verschillen op manieren die je uit het hoofd makkelijk net iets verkeerd doet. CitationEasy draait **citeproc-js** op de officiële Citation Style Language-definities — dezelfde engine die Zotero en Mendeley gebruiken — zodat de uitvoer deterministisch is en overeenkomt met wat het inzendsysteem van een tijdschrift verwacht. Metadata komt van Crossref, Open Library en PubMed; er wordt niets verzonnen, en een veld dat bij de bron ontbreekt, blijft ontbreken in plaats van te worden ingevuld met een aannemelijke gok.",
    generateHeading: "Een bronvermelding genereren",
    generateBody:
      "Stuur `inputs` in plaats van `input` voor maximaal 10 bronnen in één aanroep, of `entry` met CSL-JSON-metadata die je al hebt om de opzoeking helemaal over te slaan. Voeg `note: true` toe voor de voetnootvorm. Fouten per bron komen terug bij die bron, dus een batch faalt nooit in zijn geheel.",
    mcpHeading: "Verbinden via MCP",
    mcpIntro:
      "Het Model Context Protocol-eindpunt op `/api/mcp` spreekt JSON-RPC 2.0 via streamable HTTP en heeft geen inloggegevens nodig.",
    mcpToolsHeading: "Tools die het aanbiedt",
    endpointsHeading: "Eindpunten",
    colEndpoint: "Eindpunt",
    colWhat: "Wat het doet",
    endpointCite:
      "Maakt tot 10 bronnen tegelijk op. Accepteert een URL, DOI, ISBN, PubMed-ID, arXiv-ID, BibTeX-/RIS-/CSL-JSON-tekst, of een CSL-JSON-entry-object. Geeft de literatuurvermelding terug, en optioneel de verwijzing in de tekst en de voetnoot.",
    endpointStyles:
      "Doorzoek alle {count} stijlen op naam, opmaakklasse, discipline of regio.",
    endpointStyleSlug:
      "Eén stijl volledig uitgewerkt, met echte opgemaakte voorbeelden geproduceerd door de eigen CSL-definitie.",
    endpointSearch:
      "Op relevantie gerangschikte zoekopdracht over alle pagina's van de site.",
    endpointPages:
      "De volledige sitecatalogus als gestructureerde items, filterbaar op paginatype.",
    endpointHowToCite:
      "Elke brontype × stijl-handleidingpagina, filterbaar op beide.",
    endpointTools:
      "Elke gratis tool, met de bijbehorende categorie en zoekwoorden.",
    endpointGuides:
      "Elke educatieve handleiding, met de bijbehorende categorie.",
    endpointConverters:
      "Elke identificatie- en formaatconversie die de site biedt.",
    endpointComparisons:
      "Stijlen naast elkaar vergeleken, gekoppeld aan beide stijlrecords.",
    endpointFields:
      'Academische vakgebieden, onderwerpscategorieën en regio\'s — voor het beantwoorden van "welke stijl moet ik gebruiken?".',
    endpointFaq:
      "Geverifieerde antwoorden op veelgestelde vragen over bronvermeldingen, filterbaar met q.",
    endpointGlossary:
      "Termen over citeren en academisch schrijven met hun volledige definities — het antwoord bevat al de tekst, dus is er achteraf geen pagina nodig om op te halen.",
    endpointIndex:
      "Document voor eindpuntontdekking. Begin hier als je aan het verkennen bent.",
    discoveryHeading: "Ontdekkingsbestanden",
    discoveryLlms:
      "Samengesteld overzicht van de site, geschreven voor taalmodellen.",
    discoveryLlmsFull:
      "Elke pagina die de site publiceert, gegroepeerd per type, één regel per pagina.",
    discoveryOpenapi: "OpenAPI 3.1-beschrijving van de volledige publieke API.",
    discoveryMcp:
      "MCP-eindpunt (streamable HTTP). Doe er een GET op in een browser voor een beschrijving.",
    discoveryWellKnownMcp:
      "MCP-ontdekkingsdocument voor agents die een domein aftasten.",
    discoveryAiPlugin:
      "Verouderd plugin-manifest, voor frameworks die er nog een verwachten.",
    discoveryMd: "Markdown-weergave van elke pagina op de site.",
    discoverySitemap: "Elke indexeerbare URL.",
    catalogHeading: "Wat de catalogus bevat",
    catalogIntro:
      "Elke pagina hieronder is bereikbaar als HTML, als markdown onder `/md/` en als gestructureerd item via `/api/v1/pages`.",
    catalogStyles:
      "**{count}** citatiestijlen, elk met echte voorbeelden gegenereerd uit de eigen CSL-definitie",
    catalogTools: "**{count}** gratis browsertools",
    catalogGuides: "**{count}** educatieve handleidingen",
    catalogHowToCite: "**{count}** pagina's over hoe te citeren",
    catalogComparisons: "**{count}** stijlvergelijkingen",
    catalogConverters: "**{count}** converters",
    catalogFields: "**{count}** academische vakgebieden",
    catalogStyleLandings:
      "**{count}** hubs voor de belangrijkste stijlen met volledige opmaakregels",
    faqHeading: "Veelgestelde vragen",
    faqFreeQ: "Is de CitationEasy API gratis, en heb ik een API-sleutel nodig?",
    faqFreeA:
      "Ze is gratis en er is geen API-sleutel nodig. Elk eindpunt onder /api/v1 vereist geen authenticatie en staat open voor CORS, dus je kunt het aanroepen vanuit een browser, een notebook of een agent zonder te registreren. Er is geen eigen ratelimiet, maar identificatie-opzoekingen lopen via een proxy naar Crossref, Open Library en PubMed, die hun eigen redelijk-gebruiklimieten toepassen — bundel aanvragen met `inputs` in plaats van veel parallelle verzoeken te versturen.",
    faqLlmQ: "Worden de bronvermeldingen gegenereerd door een taalmodel?",
    faqLlmA:
      "Nee. De opmaak draait citeproc-js op de officiële Citation Style Language-definities, dezelfde engine die referentiebeheerders gebruiken. Dezelfde invoer en stijl leveren altijd dezelfde tekst op. Bibliografische metadata komt van Crossref (DOI's), Open Library (ISBN's) en PubMed (PMID's) — CitationEasy verzint geen velden, dus wat bij de bron ontbreekt, ontbreekt ook in de uitvoer in plaats van te worden geraden.",
    faqMcpQ: "Hoe verbind ik een agent met CitationEasy via MCP?",
    faqMcpA:
      "Wijs een willekeurige Model Context Protocol-client naar https://www.citationeasy.com/api/mcp met gebruik van het streamable-HTTP-transport. Er zijn geen inloggegevens nodig. In Claude Code: `claude mcp add --transport http citationeasy https://www.citationeasy.com/api/mcp`. De server biedt tools voor het genereren van bronvermeldingen, het doorzoeken van de {count} stijlen, hulp per brontype, sitezoekopdrachten en het lezen van elke pagina als markdown.",
    faqStylesQ: "Welke citatiestijlen kan de API produceren?",
    faqStylesA:
      'Alle {count} stijlen uit de Citation Style Language-repository, waaronder APA 7, MLA 9, beide Chicago-systemen, Harvard, Vancouver, IEEE, AMA en duizenden individuele tijdschriftstijlen. Geef de stijl-slug op in het veld `style`. Vriendelijke slugs zoals "chicago" en "harvard" worden herleid naar de juiste CSL-variant, en /api/v1/styles?q= vindt de rest.',
    faqLlmsTxtQ: "Wat is llms.txt en waar staat dat van CitationEasy?",
    faqLlmsTxtA:
      "llms.txt is een platteksten-index van een site, geschreven voor taalmodellen in plaats van browsers. Die van CitationEasy staat op /llms.txt; de volledige versie met elke pagina van de site staat op /llms-full.txt.",
    faqMarkdownQ:
      "Kan ik CitationEasy-pagina's als markdown lezen in plaats van als HTML?",
    faqMarkdownA:
      "Ja. Zet /md/ voor elk pad op de site — bijvoorbeeld /md/citation-style/apa of /md/tools/word-counter. Datagestuurde pagina's geven hun volledige inhoud terug, inclusief de echte opgemaakte voorbeelden die zijn gegenereerd uit de eigen CSL-definitie van elke stijl. Handgeschreven tekstpagina's (handleidingen, spiekbriefjes) geven een samenvattingskaart terug plus de canonieke HTML-URL, omdat hun tekst in de pagina zelf leeft.",
    faqReuseQ:
      "Mag ik de uitvoer van CitationEasy in mijn eigen product gebruiken?",
    faqReuseA:
      "Ja. Een opgemaakte bronvermelding is een feit over een bron; gebruik hem vrij. De CSL-stijldefinities zelf komen van het Citation Style Language-project onder de licentie CC BY-SA 3.0. Vermelding van CitationEasy wordt gewaardeerd maar is niet verplicht.",
    missingHeading: "Mis je iets?",
    missingBody:
      "Als je agent een eindpunt, veld of formaat nodig heeft dat hier niet staat, [laat het ons weten](/contact). Blader je liever handmatig? [Begin bij de bronvermeldingengenerator](/).",
  },

  // /quiz. The client component reads these as a prop rather than through a context:
  // it is one component, not the generator's nine, so there is nothing to drill through.
  quizPage: {
    metaTitle: "Welke citatiestijl moet ik gebruiken? - Gratis stijlquiz",
    metaDescription:
      "Beantwoord een paar korte vragen over je vakgebied, het type werkstuk en je voorkeuren om de beste citatiestijl voor je werkstuk te vinden. APA, MLA, Chicago, Vancouver en meer.",
    ogTitle: "Welke citatiestijl moet ik gebruiken?",
    ogDescription:
      "Doe onze gratis quiz om de beste citatiestijl voor je werkstuk te vinden.",
    breadcrumbHome: "CitationEasy",
    breadcrumbCurrent: "Stijlquiz",
    backLink: "← Terug naar generator",
    heading: "Welke citatiestijl moet ik gebruiken?",
    intro:
      "Beantwoord een paar korte vragen en we bevelen de beste citatiestijl voor je werkstuk aan.",
    progress: "Vraag {current} van {total}",
    qDiscipline: "Wat is je vakgebied?",
    optSocialScience: "Psychologie / Sociale wetenschappen",
    optHumanities: "Letterkunde / Geesteswetenschappen",
    optHistory: "Geschiedenis / Kunstgeschiedenis",
    optMedical: "Geneeskunde / Verpleegkunde / Gezondheidszorg",
    optEngineering: "Techniek / Informatica",
    optNaturalScience: "Natuurwetenschappen (biologie, scheikunde)",
    optLaw: "Rechten",
    optBusiness: "Bedrijfskunde / Economie",
    qPaperType: "Wat voor soort werkstuk schrijf je?",
    optResearch: "Onderzoekspaper of scriptie",
    optReview: "Literatuuronderzoek",
    optReport: "Labverslag of technisch rapport",
    optEssay: "Essay of opdracht voor een vak",
    optJournal: "Inzending voor een tijdschriftartikel",
    qRegion: "Waar bevindt je instelling zich?",
    optUs: "Verenigde Staten",
    optUk: "Verenigd Koninkrijk / Australië",
    optCanada: "Canada",
    optEurope: "Europa (exclusief VK)",
    optOther: "Anders",
    qPreference: "Heb je een voorkeur voor het citatieformaat?",
    optAuthorDate: "Verwijzingen in de tekst (Auteur, Jaar)",
    optNumeric: "Genummerde verwijzingen [1]",
    optFootnotes: "Voetnoten",
    optNoPreference: "Geen voorkeur",
    styleVancouver: "Vancouver",
    styleAma: "AMA (American Medical Association)",
    styleIeee: "IEEE",
    styleBluebook: "Bluebook",
    styleCse: "CSE (Council of Science Editors)",
    styleChicagoNotes: "Chicago (noten en bibliografie)",
    styleMla: "MLA (9e editie)",
    styleApa: "APA (7e editie)",
    styleHarvard: "Harvard",
    reasonVancouverMedical:
      "Vancouver is de standaard voor tijdschriften in de geneeskunde en gezondheidswetenschappen en gebruikt genummerde verwijzingen.",
    reasonAma:
      "AMA wordt veel gebruikt in medische tijdschriften en gebruikt genummerde bronvermeldingen.",
    reasonIeee:
      "IEEE is de standaard citatiestijl voor techniek, informatica en IT.",
    reasonBluebook:
      "De Bluebook is de gezaghebbende citatiestijl voor juridisch schrijven. De Chicago-stijl is het beste algemene alternatief.",
    reasonCse: "CSE is de standaard voor biologie en natuurwetenschappen.",
    reasonChicagoHistory:
      "Chicago noten en bibliografie is de gouden standaard voor geschiedenis en kunstgeschiedenis en gebruikt voetnoten voor bronvermeldingen.",
    reasonChicagoHumanities:
      "Chicago noten en bibliografie werkt goed voor geesteswetenschappen dankzij de aanpak met voetnoten.",
    reasonMla:
      "MLA is de standaard voor letterkunde, talen en geesteswetenschappen.",
    reasonApaBusiness:
      "APA wordt veel gebruikt in bedrijfskunde, economie en management.",
    reasonHarvard:
      "Harvard-verwijzing is de meest gebruikte stijl aan universiteiten in het VK en Australië.",
    reasonChicagoFootnotes:
      "Chicago noten en bibliografie gebruikt voetnoten, ideaal voor gedetailleerde bronvermeldingen.",
    reasonVancouverNumeric:
      "Vancouver gebruikt genummerde bronvermeldingen, waardoor je tekst overzichtelijk en leesbaar blijft.",
    reasonApaDefault:
      "APA is de meest gebruikte citatiestijl binnen de sociale wetenschappen, het onderwijs en veel andere disciplines.",
    recommendLabel: "Wij raden aan",
    generateButton: "{style}-bronvermeldingen maken",
    retakeButton: "Quiz opnieuw doen",
    guidesLink: "Blader door onze citeerhandleidingen voor meer hulp →",
    previousButton: "← Vorige vraag",
  },

  // /search — the server-rendered site search.
  searchPage: {
    metaTitle: "Zoeken op CitationEasy",
    metaDescription:
      "Doorzoek alle pagina's van CitationEasy tegelijk: {count} citatiestijlen, 250+ gratis tools, handleidingen, pagina's over hoe te citeren, stijlvergelijkingen, converters en de woordenlijst.",
    keyword1: "citatiestijlen zoeken",
    keyword2: "citeerhandleiding vinden",
    keyword3: "citatietool zoeken",
    ogDescription:
      "Doorzoek alle pagina's van de site tegelijk — stijlen, tools, handleidingen, pagina's over hoe te citeren, vergelijkingen, converters en termen uit de woordenlijst.",
    ogImageSubtitle:
      "Elke citatiestijl, tool, handleiding, hoe-te-citeren-pagina, vergelijking, converter en term uit de woordenlijst — in één zoekopdracht.",
    ogKind: "Zoeken",
    breadcrumb: "Zoeken",
    heading: "Zoeken op CitationEasy",
    intro:
      "Elke citatiestijl, tool, handleiding, hoe-te-citeren-pagina, vergelijking, converter en term uit de woordenlijst van de site — in één zoekopdracht.",
    inputLabel: "Zoekopdracht",
    inputPlaceholder:
      "bijv. een podcast citeren in APA, hangende inspringing, DOI",
    submit: "Zoeken",
    browseHeading: "Of blader per onderdeel",
    browseStyles: "Citatiestijlen",
    browseStylesNote: "{count} stijlen",
    browseTools: "Gratis tools",
    browseToolsNote: "{count} tools",
    browseHowTo: "Hoe citeer je elke bron",
    browseHowToNote: "{count} pagina's",
    browseGuides: "Handleidingen",
    browseGuidesNote: "{count} handleidingen",
    browseGlossary: "Woordenlijst",
    browseGlossaryNote: "{count} termen",
    browseComparisons: "Stijlvergelijkingen",
    browseComparisonsNote: "{count} vergelijkingen",
    browseConverters: "Converters",
    browseConvertersNote: "{count} converters",
    browseCheatSheets: "Spiekbriefjes",
    browseCheatSheetsNote: "{count} spiekbriefjes",
    facetsLabel: "Filteren op paginatype",
    facetAll: "Alle ({count})",
    noMatches: "Geen pagina's komen overeen met “{query}”.",
    matchesOne: "{count} pagina komt overeen met “{query}”.",
    matchesMany: "{count} pagina's komen overeen met “{query}”.",
    pagerLabel: "Resultaatpagina's",
    pagerPrevious: "← Vorige",
    pagerNext: "Volgende →",
    pagerPosition: "Pagina {page} van {total}",
    paletteLoading: "Zoeken…",
    paletteError: "Zoeken is momenteel niet beschikbaar. Probeer het opnieuw.",
    paletteViewAll: "Bekijk alle resultaten voor ‘{query}’",
    apiNote:
      "Ben je iets aan het bouwen? Dezelfde index is beschikbaar als JSON op [/api/v1/search](/api/v1/search?q=podcast) en via MCP — zie [de documentatie voor agents](/ai).",
  },

  // /cite-this-page. The six FAQ answers used to exist twice — once in the layout for the
  // FAQPage markup and once in the page for the visible list — which is exactly the drift
  // this dictionary removes: both now read the same keys.
  citeThisPage: {
    metaTitle:
      "Cite This Page - Citaties met één klik (Bookmarklet en Chrome-extensie)",
    metaDescription:
      "Citeer elke webpagina met één klik. Sleep de gratis 'Cite This Page'-bookmarklet naar je bladwijzerbalk, of installeer de gratis Chrome-extensie met een werkbalkknop en citeren via rechtermuisklik. Werkt ook op Chromebooks en beveiligde browsers.",
    ogTitle: "Cite This Page-bookmarklet en Chrome-extensie",
    ogDescription:
      "Bronvermeldingen voor websites met één klik, vanaf elke pagina — een sleep-en-neerzet-bookmarklet plus een gratis Chrome-extensie met citeren via rechtermuisklik.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbSchemaCurrent: "Cite This Page-bookmarklet",
    appName: "Cite This Page-bookmarklet",
    appDescription:
      "Een gratis bookmarklet die met één klik de webpagina citeert die je aan het lezen bent — hij opent CitationEasy met de URL van de pagina al ingevuld en maakt vervolgens de bronvermelding op in APA, MLA, Chicago en {count} andere stijlen.",
    breadcrumbLabel: "Kruimelpad",
    breadcrumbHome: "Home",
    breadcrumbCurrent: "Cite This Page-bookmarklet",
    heading: "Cite This Page: bookmarklet en Chrome-extensie",
    intro:
      "Citeer elke webpagina met één klik — zonder aanmelden. Sleep de bookmarklet hieronder naar je bladwijzerbalk (helemaal geen installatie), of haal de gratis Chrome-extensie voor een werkbalkknop en citeren via rechtermuisklik.",
    dragPrompt: "Sleep deze knop naar je bladwijzerbalk:",
    buttonTitle: "Sleep mij naar je bladwijzerbalk",
    buttonLabel: "📑 Deze pagina citeren",
    dontClick: "Klik er hier niet op — sleep hem eerst naar je bladwijzerbalk.",
    installHeading: "Zo installeer je hem",
    step1Title: "Toon je bladwijzerbalk",
    step1Body:
      "Druk in de meeste browsers op Ctrl+Shift+B (Cmd+Shift+B op Mac) om de bladwijzerbalk te tonen, zodat je ergens de knop kunt neerzetten.",
    step2Title: "Sleep de knop omhoog",
    step2Body:
      "Klik en houd de knop 'Cite This Page' hieronder ingedrukt, sleep hem dan naar je bladwijzerbalk en laat los. Hij wordt een gewone bladwijzer.",
    step3Title: "Klik erop op elke pagina",
    step3Body:
      "Klik tijdens het lezen van een artikel, nieuwsbericht of tijdschriftpagina op je nieuwe bladwijzer. CitationEasy opent met de URL van die pagina al ingevuld en de bronvermelding klaar om te kopiëren.",
    manualHeading: "Kun je hem niet slepen? Voeg hem handmatig toe",
    manualBody:
      'Maak een nieuwe bladwijzer aan, noem hem "Cite This Page" en plak dit als URL/adres:',
    howHeading: "Hoe het werkt",
    howBody:
      "De bookmarklet voert geen code uit op de pagina die je leest. Hij geeft alleen het webadres van die pagina door aan CitationEasy, waar onze server de openbare paginametadata ophaalt en analyseert — hetzelfde vertrouwde pad dat wordt gebruikt wanneer je zelf een URL in de generator plakt. Dat houdt het veilig en vermijdt de cross-origin-beperkingen die oudere bookmarklets die in de pagina zelf werkten hebben uitgefaseerd.",
    extensionHeading: "Liever een Chrome-extensie?",
    extensionBody:
      "De gratis CitationEasy-extensie voegt een werkbalkknop toe die het huidige tabblad citeert, plus opties via rechtermuisklik om een link — of een geselecteerde DOI of ISBN — te citeren zonder hem te openen. Net als de bookmarklet voert hij geen code uit op de pagina's die je bezoekt: hij geeft alleen de URL door aan CitationEasy. Hij heeft alleen de machtigingen `activeTab` en `contextMenus` nodig en werkt in Chrome, Edge, Brave en andere Chromium-browsers.",
    downloadButton: "⬇ Download de Chrome-extensie (.zip)",
    install1: "Download en pak de extensie uit.",
    install2:
      "Open `chrome://extensions` en zet **Ontwikkelaarsmodus** aan (rechtsboven).",
    install3:
      "Klik op **Uitgepakte extensie laden** en selecteer de uitgepakte map.",
    install4:
      "Zet de CitationEasy-knop vast, en klik er dan op elke pagina op om die te citeren.",
    faqHeading: "Veelgestelde vragen",
    faqWhatQ: "Wat is de 'Cite This Page'-bookmarklet?",
    faqWhatA:
      "Het is een kleine bladwijzer die je naar de bladwijzerbalk van je browser sleept. Als je erop klikt op een willekeurige webpagina, opent hij CitationEasy in een nieuw tabblad met de URL van die pagina al ingevuld, zodat je met één klik een opgemaakte bronvermelding krijgt — zonder kopiëren en plakken.",
    faqExtensionNeededQ: "Moet ik een extensie installeren?",
    faqExtensionNeededA:
      "Nee. Een bookmarklet is gewoon een bladwijzer, dus er is niets te installeren en er hoeven geen machtigingen te worden verleend. Hij werkt op beveiligde schoolchromebooks en de meeste mobiele browsers waar extensies zijn geblokkeerd. Heb je liever een extensie, dan is op deze pagina ook een gratis Chrome-extensie beschikbaar.",
    faqChromeQ: "Is er een Chrome-extensie?",
    faqChromeA:
      "Ja. De gratis CitationEasy Chrome-extensie voegt een werkbalkknop toe die het huidige tabblad citeert, plus opties via rechtermuisklik om een link of een geselecteerde DOI/ISBN te citeren zonder hem te openen. Hij voert geen code uit op de pagina's die je bezoekt en heeft alleen de machtigingen activeTab en contextMenus nodig. Download hem van deze pagina en laad hem via chrome://extensions; hij werkt in Chrome, Edge, Brave en andere Chromium-browsers.",
    faqAddQ: "Hoe voeg ik de bookmarklet toe?",
    faqAddA:
      "Zorg dat je bladwijzerbalk zichtbaar is en sleep dan de knop 'Cite This Page' op deze pagina ernaartoe. Als slepen niet mogelijk is (bijvoorbeeld op mobiel), maak dan handmatig een nieuwe bladwijzer aan en plak de bookmarkletcode als URL.",
    faqSafeQ: "Is het veilig? Leest hij de pagina waar ik op zit?",
    faqSafeA:
      "Ja, het is veilig. De bookmarklet voert geen code uit op de pagina die je leest — hij stuurt alleen het webadres van die pagina naar CitationEasy, waar onze server de openbare paginametadata ophaalt en analyseert, precies zoals wanneer je zelf een URL plakt.",
    faqOtherSourcesQ: "Werkt het ook voor andere bronnen dan websites?",
    faqOtherSourcesA:
      "De bookmarklet is voor webpagina's, maar de onderliggende link accepteert ook een DOI, ISBN, PMID of arXiv-ID via de parameter ?source= — dus een deelbare 'cite this'-link werkt ook voor tijdschriftartikelen en boeken.",
    relatedHeading: "Gerelateerde tools",
    relatedGenerator: "Bronvermeldingengenerator",
    relatedHowToCite: "Hoe citeer je een website",
    relatedTools: "Alle tools",
  },

  // /shared-bibliography — opens a bibliography that travelled inside a share link.
  sharedBibliographyPage: {
    metaTitle:
      "Gedeelde literatuurlijst - Bekijk en importeer gedeelde bronvermeldingen",
    metaDescription:
      "Open een literatuurlijst die iemand met je heeft gedeeld vanuit CitationEasy. Bekijk de opgemaakte bronvermeldingen, kopieer ze, of importeer de hele lijst in je eigen literatuurlijst — gratis, zonder account, en de gegevens zitten in de link zelf.",
    ogTitle: "Gedeelde literatuurlijst",
    ogDescription:
      "Bekijk en importeer een gedeelde literatuurlijst. De bronvermeldingen reizen mee in de link — er wordt niets op een server opgeslagen, en links verlopen nooit.",
    breadcrumbRoot: "CitationEasy",
    breadcrumbLabel: "Kruimelpad",
    breadcrumbHome: "Home",
    breadcrumbCurrent: "Gedeelde literatuurlijst",
    heading: "Gedeelde literatuurlijst",
    intro:
      "Iemand heeft deze bronvermeldingen met je gedeeld. Kopieer ze, of importeer de hele lijst met één klik in je eigen CitationEasy-literatuurlijst.",
    loading: "Gedeelde literatuurlijst wordt geopend…",
    copyAll: "Alles kopiëren",
    copied: "Gekopieerd",
    copyFailed: "Kopiëren mislukt",
    citationOne: "bronvermelding",
    citationMany: "bronvermeldingen",
    addAll: "Alles toevoegen aan mijn literatuurlijst",
    added: "{count} toegevoegd aan je literatuurlijst",
    skippedOne: "{count} duplicaat overgeslagen",
    skippedMany: "{count} duplicaten overgeslagen",
    openGenerator: "open de generator",
    emptyHeading: "Geen gedeelde literatuurlijst in deze link",
    emptyBody:
      "Deze pagina toont literatuurlijsten die vanuit CitationEasy zijn gedeeld. Om die van jou te delen, open je je literatuurlijst in de [bronvermeldingengenerator](/), kies je **Exporteren → Deellink kopiëren**, en stuur je de link naar wie je maar wilt. De bronvermeldingen reizen mee in de link zelf — er wordt niets op een server opgeslagen, dus links blijven voor altijd werken en je gegevens blijven privé.",
    decodeError: "Deze deellink kon niet worden geopend.",
  },

  // The 19 source types the 15 detailed styles show worked examples for
  // (`DetailedStyleData.examples[].sourceType`). 75 rows, 19 distinct values — so this
  // is a vocabulary, not per-page copy, and it belongs here rather than repeated in
  // every locale's `src/content/style-pages/<locale>.json`.
  //
  // Two casings for the same reason `howToCitePage.refList*Lower` has two: the pages
  // print the label as a heading ("Book") and inside a sentence ("a sample APA entry
  // for a book"). Lower-casing the translated noun in code is wrong in German, where
  // every one of these is capitalised, so the lower form is a real key a translator
  // fills rather than a `.toLowerCase()` call.
  //
  // The citations themselves — `referenceEntry` and `inTextCitation` — are NOT here and
  // are not translatable anywhere: they are worked examples in a specific style's own
  // punctuation. See `src/lib/localized-content.ts`.
  sourceTypes: {
    book: "Boek",
    bookLower: "boek",
    bookChapter: "Boekhoofdstuk",
    bookChapterLower: "boekhoofdstuk",
    case: "Rechtszaak",
    caseLower: "rechtszaak",
    conferencePaper: "Congresbijdrage",
    conferencePaperLower: "congresbijdrage",
    courtCase: "Gerechtelijke uitspraak",
    courtCaseLower: "gerechtelijke uitspraak",
    federalStatute: "Federale wet",
    federalStatuteLower: "federale wet",
    governmentDocument: "Overheidsdocument",
    governmentDocumentLower: "overheidsdocument",
    governmentReport: "Overheidsrapport",
    governmentReportLower: "overheidsrapport",
    journalArticle: "Tijdschriftartikel",
    journalArticleLower: "tijdschriftartikel",
    lawReviewArticle: "Juridisch tijdschriftartikel",
    lawReviewArticleLower: "juridisch tijdschriftartikel",
    legislation: "Wetgeving",
    legislationLower: "wetgeving",
    newspaperArticle: "Krantenartikel",
    newspaperArticleLower: "krantenartikel",
    patent: "Octrooi",
    patentLower: "octrooi",
    podcast: "Podcast",
    podcastLower: "podcast",
    preprint: "Preprint",
    preprintLower: "preprint",
    standard: "Norm",
    standardLower: "norm",
    thesis: "Scriptie",
    thesisLower: "scriptie",
    website: "Website",
    websiteLower: "website",
    youtubeVideo: "YouTube-video",
    youtubeVideoLower: "YouTube-video",
  },

  // Chrome shared by the five per-style guide routes — /examples, /format,
  // /in-text-citation, /reference-page and /annotated-bibliography, 15 styles each.
  //
  // One section rather than five copies: the CTA, the back link and the cross-links
  // between the five pages are the same sentences on every one of them, and duplicating
  // them per route would be five identical translation units instead of one.
  styleGuidePages: {
    backToStyle: "Terug naar {style}",
    backToGenerator: "Terug naar de bronvermeldingengenerator",
    ctaHeading: "Genereer automatisch {style}-bronvermeldingen",
    ctaButton: "Citeer nu in {style} →",
    faqHeading: "Veelgestelde vragen",
    resourcesHeading: "Meer bronnen",
    relatedHeading: "Gerelateerde {style}-handleidingen",
    resourceGenerator:
      "Gratis generator voor bronvermeldingen ({count} stijlen)",
    resourceTools: "Gratis schrijfhulpmiddelen",
    resourceGuides: "Alle citeerhandleidingen",
    resourceStyleGenerator: "{style}-generator voor bronvermeldingen",
    resourceExamples: "{style}-citatievoorbeelden",
    resourceFormat: "{style}-opmaakgids",
    resourceInText: "{style}-handleiding voor verwijzingen in de tekst",
    resourceAnnotatedBib: "{style}-handleiding voor geannoteerde bibliografie",
    resourceReferencePage: "{style}-gids voor {list}",
    notFoundTitle: "Niet gevonden",
  },

  // /examples/<style> — the copy-ready worked examples for all 15 detailed styles.
  examplesPage: {
    metaTitle: "{style}-citatievoorbeelden - Alle brontypen [2026]",
    metaDescription:
      "Bekijk opgemaakte {style}-citatievoorbeelden ({edition}) voor boeken, tijdschriftartikelen, websites en meer. Kopieerklare vermeldingen voor {list} en verwijzingen in de tekst.",
    ogTitle: "{style}-citatievoorbeelden",
    ogDescription:
      "Kopieerklare {style}-vermeldingen voor elk veelvoorkomend brontype.",
    ogImageTitle: "{style}-citatievoorbeelden",
    ogKind: "{style} {edition}",
    keywordExamples: "{style}-citatievoorbeelden",
    keywordReference: "{style}-referentievoorbeelden",
    keywordInText: "voorbeelden van {style}-verwijzingen in de tekst",
    keywordList: "voorbeelden van {listLower} in {style}",
    keywordHowTo: "hoe citeer je in {style}",
    breadcrumb: "{style}-citatievoorbeelden",
    heading: "{style}-citatievoorbeelden",
    lead: "Bekijk correct opgemaakte {style}-citatievoorbeelden ({edition}) voor {count} veelvoorkomende brontypen. Elk voorbeeld bevat zowel de vermelding voor {listLower} als de bijbehorende verwijzing in de tekst.",
    entryLabel: "Vermelding voor {list}:",
    inTextLabel: "Verwijzing in de tekst:",
    ctaBody:
      "Sla de handmatige opmaak over. Plak een DOI, URL of ISBN in CitationEasy en krijg in enkele seconden een perfect opgemaakte {style}-bronvermelding.",
    faqLookQ: "Hoe ziet een {style}-bronvermelding eruit?",
    faqLookA:
      "Een {style}-bronvermelding bestaat uit een vermelding voor {listLower} met de volledige brongegevens en een verwijzing in de tekst. De opmaak verschilt per brontype. Een boekvermelding in {style} ({edition}) bevat bijvoorbeeld de auteur, het jaar, de titel en de uitgever.",
    faqHowManyQ: "Hoeveel {style}-citatievoorbeelden zijn er beschikbaar?",
    faqHowManyA:
      "CitationEasy biedt {style}-citatievoorbeelden voor {count} brontypen, waaronder {types}. Elk voorbeeld bevat zowel de vermelding voor {listLower} als de bijbehorende verwijzing in de tekst.",
    faqCopyQ: "Kan ik deze {style}-citatievoorbeelden kopiëren?",
    faqCopyA:
      "Ja, de {style}-voorbeelden op deze pagina tonen de exacte opmaak voor elk brontype. Je kunt ze als sjabloon gebruiken voor je eigen bronvermeldingen. Gebruik voor automatische opmaak onze gratis {style}-generator voor bronvermeldingen.",
  },

  // /format/<style> — font, margins, spacing, title page and headers. The VALUES those
  // labels take are per-style prose and live in the overlay, not here.
  formatPage: {
    metaTitle:
      "{style}-opmaakgids - Regels voor de opmaak van je werkstuk [2026]",
    metaDescription:
      "Complete opmaakgids voor een werkstuk in {style} ({edition}). Leer het juiste lettertype, de marges, de regelafstand, de titelpagina, de koppen en de opmaakregels voor {listLower}.",
    ogTitle: "Opmaak van een werkstuk in {style}",
    ogDescription:
      "Marges, lettertype, regelafstand en titelpagina voor een werkstuk in {style}.",
    ogImageTitle: "Opmaak van een werkstuk in {style}",
    ogImageSubtitle: "Marges: {margins} · {spacing}",
    ogKind: "{style} {edition}",
    keywordFormat: "{style}-opmaak",
    keywordPaperFormat: "opmaak werkstuk {style}",
    keywordRules: "{style}-opmaakregels",
    keywordMargins: "{style} marges lettertype regelafstand",
    keywordList: "opmaak {listLower} {style}",
    breadcrumb: "{style}-opmaakgids",
    heading: "{style}-opmaakgids",
    lead: "Volg deze opmaakregels voor een werkstuk in {style} ({edition}) om ervoor te zorgen dat je document aan de vereiste normen voldoet. Deze gids behandelt lettertype, marges, regelafstand, titelpagina, koppen en de opmaak voor {listLower}.",
    paperHeading: "Opmaak van het werkstuk",
    paperIntro:
      "Hieronder staan de belangrijkste opmaakeisen voor werkstukken geschreven in {style}-stijl ({edition}).",
    labelFont: "Lettertype",
    labelFontSize: "Lettergrootte",
    labelMargins: "Marges",
    labelSpacing: "Regelafstand",
    labelTitlePage: "Titelpagina",
    labelHeaders: "Koppen / paginanummers",
    listHeading: "Opmaak van {list}",
    listIntro:
      "Volg deze regels om {listLower} correct op te maken in {style}-stijl:",
    ctaBody:
      "Laat CitationEasy de opmaak regelen. Plak een DOI, URL of ISBN en krijg in enkele seconden een perfect opgemaakte {style}-bronvermelding.",
    faqFontQ:
      "Welk lettertype en welke grootte moet ik gebruiken voor {style}-opmaak?",
    faqFontA:
      "{style} ({edition}) vereist {font}. De standaard lettergrootte is {fontSize}.",
    faqMarginsQ: "Wat zijn de margevereisten voor {style}?",
    faqMarginsA:
      "Werkstukken in {style} vereisen {margins}. Dit geldt voor de boven-, onder-, linker- en rechtermarge van elke pagina.",
    faqTitlePageQ: "Vereist {style} een titelpagina?",
  },

  // /in-text-citation/<style> — the six in-text forms. The forms themselves
  // ("(Smith, 2024)") are per-style prose in the overlay.
  inTextCitationPage: {
    metaTitle:
      "{style}-handleiding voor verwijzingen in de tekst - Regels en voorbeelden [2026]",
    metaDescription:
      "Leer hoe je {style}-verwijzingen ({edition}) in de tekst opmaakt. Regels voor één auteur, meerdere auteurs, directe citaten, geen auteur en geen datum, met voorbeelden.",
    ogTitle: "{style}-verwijzingen in de tekst",
    ogDescription:
      "Elke vorm van {style}-verwijzing in de tekst, met voorbeelden.",
    ogImageTitle: "{style}-verwijzingen in de tekst",
    ogImageSubtitle: "Eén auteur {singleAuthor} · drie of meer {threeOrMore}",
    ogKind: "{style} {edition}",
    keywordInText: "{style}-verwijzing in de tekst",
    keywordExamples: "voorbeelden van {style}-verwijzingen in de tekst",
    keywordParenthetical: "{style}-verwijzing tussen haakjes",
    keywordRules: "{style}-citatieregels",
    keywordHowTo: "hoe citeer je in de tekst {style}",
    breadcrumb: "{style}-verwijzingen in de tekst",
    heading: "{style}-verwijzingen in de tekst",
    lead: "Beheers {style}-verwijzingen ({edition}) in de tekst met deze complete handleiding. Leer de juiste opmaak voor elk scenario, inclusief één en meerdere auteurs, directe citaten en bronnen met ontbrekende informatie.",
    rulesHeading: "Regels voor verwijzingen in de tekst",
    rulesIntro:
      "Hieronder staan de {style}-regels voor verwijzingen in de tekst voor elk veelvoorkomend scenario dat je tegenkomt bij het schrijven van je werkstuk.",
    labelSingleAuthor: "Eén auteur",
    labelTwoAuthors: "Twee auteurs",
    labelThreeOrMore: "Drie of meer auteurs",
    labelDirectQuote: "Direct citaat",
    labelNoAuthor: "Geen auteur",
    labelNoDate: "Geen datum",
    examplesHeading: "Snelle voorbeelden",
    examplesIntro:
      "Hier zijn praktijkvoorbeelden van {style}-verwijzingen in de tekst voor verschillende brontypen:",
    ctaBody:
      "Maak je geen zorgen meer over de opmaak van verwijzingen in de tekst. Plak een DOI, URL of ISBN in CitationEasy en krijg direct zowel de {listLower}-vermelding als de verwijzing in de tekst.",
    faqFormatQ: "Hoe maak ik een {style}-verwijzing in de tekst op?",
    faqFormatA:
      "Voor één auteur in {style} gebruik je het formaat {singleAuthor}. Voor twee auteurs gebruik je {twoAuthors}. Voor drie of meer auteurs gebruik je {threeOrMore}.",
    faqQuoteQ: "Hoe citeer ik een direct citaat in {style}?",
    faqQuoteA:
      "Gebruik voor directe citaten in {style} ({edition}) het formaat: {directQuote}.",
    faqMissingQ: "Wat als er geen auteur of geen datum is in {style}?",
    faqMissingA:
      "Als er geen auteur is in {style}, gebruik je: {noAuthor}. Als er geen datum is, gebruik je: {noDate}.",
  },

  // /reference-page/<style> — how to build the list at the back of the paper.
  //
  // The five `mistake*` strings carry `**bold**` inline markup because the lead-in of
  // each bullet is emphasised inside the sentence; they render through `InlineText`.
  referenceListPage: {
    metaTitle: "{style}-gids voor {list} - Opmaakregels [2026]",
    metaDescription:
      "Leer hoe je {listLower} opmaakt in {style} ({edition}). Stapsgewijze opmaakregels, voorbeelden en veelgemaakte fouten om te vermijden voor {fullName}.",
    ogTitle: "{listLower} in {style}",
    ogDescription: "Zo bouw je {list} op in {style}.",
    ogImageTitle: "{list} in {style}",
    ogKind: "{style} {edition}",
    breadcrumb: "Zo maak je {list} op in {style}",
    heading: "Zo maak je {list} op in {style}",
    lead: "De sectie {listLower} is een van de belangrijkste onderdelen van elk academisch werkstuk dat is opgemaakt in {style} ({edition}). Ze geeft lezers de volledige gegevens van elke bron die je hebt geciteerd, zodat ze je bronvermeldingen kunnen terugvinden en verifiëren. De opmaak correct krijgen is essentieel voor academische geloofwaardigheid. Deze gids behandelt de exacte regels, toont echte voorbeelden en belicht de meest voorkomende fouten die studenten maken.",
    rulesHeading: "Opmaakregels",
    rulesIntro: "Volg deze regels bij het maken van {listLower} in {style}:",
    exampleHeading: "Voorbeeld van {list}",
    exampleIntro:
      "Zo ziet de sectie {listLower} in {style} eruit, met vermeldingen voor verschillende brontypen:",
    mistakesHeading: "Veelgemaakte fouten om te vermijden",
    mistakesIntro:
      "Dit zijn de vijf meest voorkomende opmaakfouten die studenten maken bij het opstellen van {listLower} in {style}:",
    mistakeIndent:
      "**Ontbrekende of onjuiste hangende inspringing:** bij elke vermelding moet de eerste regel links uitgelijnd staan en moeten alle volgende regels 0,5 inch inspringen. Gebruik de alinea-instellingen van je tekstverwerker in plaats van op de Tab-toets te drukken.",
    mistakeOrder:
      '**Onjuiste alfabetisering of volgorde:** vermeldingen moeten worden gerangschikt volgens de regels van {style}. Negeer lidwoorden zoals "A," "An" en "The" bij het alfabetiseren. Vermeld bij genummerde stijlen de items in de volgorde waarin ze voor het eerst in de tekst voorkomen.',
    mistakeSpacing:
      "**Inconsistente regelafstand:** controleer de regelafstand in de hele sectie {listLower} nog eens. Enkele en dubbele regelafstand door elkaar gebruiken is een veelgemaakte fout die makkelijk over het hoofd wordt gezien.",
    mistakeItalics:
      "**Verkeerd gebruik van cursief:** zet titels van zelfstandige werken zoals boeken en tijdschriften cursief, maar niet de titels van artikelen of hoofdstukken. Controleer de richtlijnen van {style} om precies te weten welke elementen cursief moeten staan.",
    mistakeLinks:
      "**Ontbrekende of niet-werkende DOI's en URL's:** neem altijd een DOI op als die beschikbaar is, opgemaakt als klikbare link. Test elke URL voordat je je werkstuk inlevert, om er zeker van te zijn dat hij naar de juiste bron leidt.",
    ctaHeading: "Genereer automatisch {list}",
    ctaBody:
      "Sla de handmatige opmaak over. Plak een DOI, URL of ISBN in CitationEasy en krijg in enkele seconden een perfect opgemaakte {style}-bronvermelding, klaar voor {listLower}.",
    faqWhatQ: "Wat is {list} in {style}?",
    faqWhatA:
      "In {style} verwijst de term {list} naar het onderdeel aan het einde van je werkstuk dat alle bronnen vermeldt die je in de tekst hebt geciteerd, opgemaakt volgens de richtlijnen van {fullName} ({edition}). Het stelt lezers in staat elke geciteerde bron terug te vinden en te verifiëren.",
    faqFormatQ: "Hoe maak ik de sectie {listLower} op in {style}?",
    faqFormatA:
      "Begin op een nieuwe pagina, geef die de titel “{list}” en vermeld alle bronnen die je in je werkstuk hebt geciteerd. Volg de specifieke {style}-regels voor hangende inspringing, regelafstand, alfabetische of numerieke volgorde en cursivering.",
    faqDifferenceQ:
      "Wat is het verschil tussen {listLower} en een bibliografie?",
    faqDifferenceA:
      "De sectie {listLower} bevat alleen de bronnen die je rechtstreeks in je werkstuk hebt geciteerd, terwijl een bibliografie ook bronnen kan bevatten die je hebt geraadpleegd maar niet hebt geciteerd. In {style} ({edition}) heet de lijst aan het einde van het werkstuk de “{list}”.",
  },

  // /annotated-bibliography/<style>. `whatTypes` carries `**bold**` inline markup.
  //
  // `exampleAnnotation` is a specimen annotation, not a citation: it is ordinary prose
  // about an imaginary source and translates like any other paragraph. The reference
  // entry printed above it does not — that comes from `DETAILED_STYLES` and stays as
  // the style wrote it.
  annotatedBibPage: {
    metaTitle:
      "{style}-handleiding en voorbeelden voor geannoteerde bibliografie [2026]",
    metaDescription:
      "Leer hoe je een geannoteerde bibliografie schrijft in {style} ({edition}). Inclusief opmaakregels, voorbeeldvermeldingen en tips voor geannoteerde bibliografieën in {fullName}.",
    ogTitle: "Geannoteerde bibliografie in {style}",
    ogDescription: "Hoe je een geannoteerde bibliografie opmaakt in {style}.",
    ogImageTitle: "Geannoteerde bibliografie in {style}",
    ogKind: "{style} {edition}",
    breadcrumb: "Geannoteerde bibliografie in {style}",
    heading: "Geannoteerde bibliografie in {style}",
    lead: "Een geannoteerde bibliografie is een lijst van bronvermeldingen die elk een korte beschrijvende of evaluerende alinea bevatten. In {style}-opmaak ({edition}) begint elke vermelding met een correct opgemaakte literatuurverwijzing, gevolgd door een annotatie die de bron samenvat, beoordeelt of erop reflecteert. Deze handleiding leidt je door de regels, de opmaak en de voorbeelden die je nodig hebt om een verzorgde geannoteerde bibliografie in {style} te maken.",
    whatHeading: "Wat is een geannoteerde bibliografie?",
    whatBody:
      "Een geannoteerde bibliografie gaat verder dan de gewone sectie {listLower} door na elke bronvermelding een korte alinea toe te voegen. Deze annotatie geeft lezers inzicht in de inhoud, kwaliteit en relevantie van de bron. Docenten geven vaak geannoteerde bibliografieën als opdracht om studenten te helpen hun bronnen kritisch te evalueren en te laten zien hoe elke bron bijdraagt aan hun onderzoeksonderwerp.",
    whatTypes:
      "Er zijn drie veelvoorkomende soorten annotaties: **beschrijvende** annotaties die de bron samenvatten, **evaluerende** annotaties die de kwaliteit en betrouwbaarheid van het werk beoordelen, en **gecombineerde** annotaties die beide doen. De instructies van je opdracht moeten aangeven welk type je moet gebruiken.",
    rulesHeading: "Regels voor geannoteerde bibliografie in {style}",
    rulesIntro:
      "Volg deze regels bij het opmaken van een geannoteerde bibliografie in {fullName}:",
    exampleHeading: "Voorbeeld van een geannoteerde-bibliografievermelding",
    exampleIntro:
      "Hieronder staat een voorbeeld van een {style}-vermelding voor een geannoteerde bibliografie voor een {sourceType}:",
    exampleAnnotation:
      "Deze bron biedt een uitgebreid overzicht van het onderwerp en presenteert goed onderbouwde argumenten die worden gestaafd door empirisch bewijs. De auteur put uit een breed scala aan primaire en secundaire bronnen om een overtuigend betoog op te bouwen. De methodologie is degelijk en de bevindingen zijn duidelijk gepresenteerd, waardoor dit werk een waardevolle bron is om het onderwerp te begrijpen. Deze bron is vooral nuttig voor het opzetten van het theoretisch kader van het onderzoeksproject en biedt perspectieven die andere werken in de bibliografie aanvullen.",
    tipsHeading: "Tips voor het schrijven van {style}-annotaties",
    tipSummarize:
      "Begin met het samenvatten van de bron in twee tot drie zinnen en voeg vervolgens je beoordeling toe van de sterke punten, beperkingen en relevantie ervan.",
    tipTone:
      "Gebruik in je annotaties steeds de derde persoon en een academische toon. Vermijd informeel taalgebruik of reflecties in de eerste persoon, tenzij je docent daarom vraagt.",
    tipLength:
      "Houd elke annotatie tussen de 100 en 200 woorden. Wees bondig en focus op de belangrijkste aspecten van de bron.",
    tipRelevance:
      "Leg uit hoe de bron past binnen je onderzoek als geheel. Beschrijf welk uniek perspectief of bewijs de bron toevoegt.",
    tipCitation:
      "Zorg dat het citatiegedeelte van elke vermelding precies de opmaakregels van {fullName} volgt voordat je de annotatie schrijft.",
    ctaHeading: "Bouw automatisch je geannoteerde bibliografie",
    ctaBody:
      "Gebruik de CitationEasy-maker voor geannoteerde bibliografieën om correct opgemaakte {style}-bronvermeldingen te genereren en voeg vervolgens je annotaties toe.",
    ctaButton: "Open de maker voor geannoteerde bibliografieën →",
    faqWhatQ: "Wat is een geannoteerde bibliografie in {style}?",
    faqWhatA:
      "Een geannoteerde bibliografie in {style} ({edition}) is een lijst van bronvermeldingen opgemaakt in {fullName}-stijl, elk gevolgd door een korte alinea die de bron samenvat, beoordeelt of erop reflecteert. Het helpt lezers de relevantie en kwaliteit van elke bron te begrijpen.",
    faqLengthQ: "Hoe lang moeten annotaties zijn?",
    faqLengthA:
      "Annotaties zijn doorgaans 100 tot 200 woorden lang. Sommige opdrachten vereisen kortere of langere annotaties, afhankelijk van de verwachte diepgang van de analyse. Controleer altijd de richtlijnen van je docent voor specifieke lengte-eisen.",
    faqIncludeQ: "Wat moet een annotatie bevatten?",
    faqIncludeA:
      "Een annotatie moet een korte samenvatting bevatten van het hoofdargument of de bevindingen van de bron, een beoordeling van de betrouwbaarheid en bruikbaarheid van de bron, en een reflectie op hoe de bron past binnen je onderzoek. Sommige annotaties zijn puur beschrijvend, terwijl andere een kritische analyse bevatten.",
  },

  guidePage: {
    byline: "Door {author}",
    published: "Gepubliceerd op {date}",
    updated: "Bijgewerkt op {date}",
    readingTime: "one:{count} minuut leestijd|other:{count} minuten leestijd",
    wordCount: "one:{count} woord|other:{count} woorden",
  },

  // /guides hub, /guides/<category> index pages, and the chrome around each guide
  guideLibrary: {
    eyebrow: "Gidsenbibliotheek",
    browseByCategory: "Bladeren per categorie",
    recentlyUpdated: "Onlangs bijgewerkt",
    allGuides: "Alle gidsen",
    guideCount: "one:{count} gids|other:{count} gidsen",
    updatedOn: "Bijgewerkt op {date}",
    minRead: "one:{count} min leestijd|other:{count} min leestijd",
    onThisPage: "Op deze pagina",
    viewAllInCategory: "Alle gidsen over {category}",
    moreInCategory: "Meer over {category}",
    aboutThisGuide: "Over deze gids",
    provenance:
      "Geschreven door {author}. Gepubliceerd op {published}; laatst bijgewerkt op {updated}.",
    categoryIntroSuffix:
      "Elke gids is gratis, geschreven voor studenten en onderzoekers en gedateerd, zodat je ziet wanneer hij voor het laatst is gecontroleerd.",
    photoCredit: "Foto: {author}, {source} ({license})",
    imageAlt: "Foto ter illustratie van de gidsen over {category}",
    imageSourceCommons: "Wikimedia Commons",
    apaStyleName: "APA-stijl",
    apaStyleDescription:
      "Websites, tijdschriftartikelen, boeken, video's en meer citeren in APA 7, met voorbeelden voor de referentielijst en verwijzingen in de tekst.",
    mlaStyleName: "MLA-stijl",
    mlaStyleDescription:
      "Elke gangbare bron citeren in MLA 9, van webpagina's en artikelen tot gedichten, toneelstukken, films en cursusmateriaal.",
    chicagoStyleName: "Chicago-stijl",
    chicagoStyleDescription:
      "Gidsen voor de Chicago Manual of Style met noten-bibliografie- en auteur-datumverwijzingen voor boeken, artikelen, archieven, overheidsdocumenten en media.",
    styleComparisonsName: "Stijlvergelijkingen",
    styleComparisonsDescription:
      "Vergelijkingen naast elkaar van APA, MLA, Chicago, Harvard, Vancouver, IEEE en meer, plus advies over welke stijl jouw vakgebied verwacht.",
    researchMethodsName: "Onderzoeksmethoden",
    researchMethodsDescription:
      "Een onderzoekspaper plannen, literatuur bestuderen, aantekeningen maken, bronnen beoordelen, plagiaat vermijden en een bibliografie opbouwen.",
    academicWritingName: "Academisch schrijven",
    academicWritingDescription:
      "Schrijfgidsen voor samenvattingen, inleidingen, conclusies, probleemstellingen, methodesecties en heldere academische tekst.",
    fieldSpecificName: "Per vakgebied",
    fieldSpecificDescription:
      "Citeergidsen per discipline, van psychologie, geschiedenis en letterkunde tot geneeskunde, techniek, rechten en verpleegkunde.",
    sourceEvaluationName: "Bronnen beoordelen",
    sourceEvaluationDescription:
      "Betrouwbaarheid beoordelen, wetenschappelijke van populaire bronnen onderscheiden, Google Scholar en databanken gebruiken en vooringenomenheid herkennen.",
    citationHelpName: "Citeerhulp",
    citationHelpDescription:
      "Antwoorden op lastige gevallen: ontbrekende auteurs of data, meerdere auteurs, pdf's, AI-tools, rechtszaken en het importeren of exporteren van citaties.",
  },

  common: {
    backToGuides: "← Terug naar de handleidingen",
    free: "Gratis",
    noSignup: "Geen account nodig",
    backToHome: "Terug naar de generator",
    inEnglish: "In het Engels",
    viewInEnglish: "Bekijk in het Engels",
    availableInEnglish:
      "Deze pagina is in het Engels beschikbaar met meer detail.",
    otherLanguages: "Andere talen",
    skipToContent: "Naar de inhoud",
    cookieNotice:
      "We gebruiken cookies voor analyse en advertenties om CitationEasy gratis te houden.",
    privacyPolicy: "Privacybeleid",
    accept: "Accepteren",
    decline: "Weigeren",
    advertisement: "Advertentie",
  },
  legalPage: {
    backLink: "← Terug naar generator",
    effective: "Ingangsdatum:",
    updated: "Laatst bijgewerkt:",
  },
};

export default nl;
